"use client";

import { useState, useEffect, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import PizZip from "pizzip";
import { PDFDocument } from "pdf-lib";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import axios from "axios";
import { saveAs } from "file-saver";
import { NumericFormat } from "react-number-format";
import AlertDialog from "../components/AlertDialog";
import Select from "react-select";
export default function SOVTab({ project, setProject }) {
  const getAddressString = (address = {}) =>
    `${address.street || ""}, ${address.city || ""}, ${address.state || ""}, ${
      address.country || ""
    } ${address.postalCode || ""}`;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#4b5563",
      borderColor: state.isFocused ? "#f97316" : "#6b7280",
      color: "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
      "&:hover": { borderColor: "#f97316" },
      minHeight: "32px",
      fontSize: "13px",
    }),
    input: (provided) => ({
      ...provided,
      color: "#e5e7eb",
      outline: "none",
      boxShadow: "none",
      border: "none",
      margin: 0,
      padding: 0,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#4b5563" : "#374151",
      color: state.isSelected ? "#f97316" : "#e5e7eb",
      "&:hover": { backgroundColor: "#6b7280" },
      fontSize: "13px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#374151",
      borderRadius: "0.375rem",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #4b5563",
      zIndex: 50,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#e5e7eb",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#4b5563",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#e5e7eb",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#e5e7eb",
      "&:hover": {
        backgroundColor: "#f97316",
        color: "#fff",
      },
    }),
  };

  const [sovData, setSovData] = useState(() => {
    const account = project.accountId || {};
    return {
      billTo: account.companyName || "",
      invoiceDate: "",
      totalInvoiced: "0.00",
      dueDate: "",
      projectName: project.projectName || "",
      job: getAddressString(project.address),
      accountName: account.companyName || "",
      accountAddress: getAddressString(account.address),
      items: [],
      withHoldback: false,
      status: "new",
      generated: false,
      qbInvoiceNumber: null,
      qbInvoiceId: null,
      sentAt: null,
      sendToEmail: project.billingContact?.email || "",
      specialInstructions: "",
      invoiceChangesSeperately: false,
      invoiceComponents: [],
      holdbackInvoiced: false,
      holdbackInvoiceAmount: "0.00",
      // NEW FIELDS
      poNumber: "",
      drawNumber: 1,
      workCompletedFrom: "",
      workCompletedTo: "",
    };
  });
  const [sovs, setSovs] = useState([]);
  const [approvedChangeOrders, setApprovedChangeOrders] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [quickBooksItems, setQuickBooksItems] = useState([]);
  const [quickBooksTaxRates, setQuickBooksTaxRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editingSovId, setEditingSovId] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState("error");
  const [alertTitle, setAlertTitle] = useState("Error");
  const [alertMessage, setAlertMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomBreakdown, setIsCustomBreakdown] = useState(false);
  const [taxRateData, setTaxRateData] = useState({
    taxRate: "0",
    taxCodeId: "",
  });
  const [customBreakdownItems, setCustomBreakdownItems] = useState([
    { description: "", amount: "0.00" },
  ]);
  const [contacts, setContacts] = useState([]);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: `Dear Client,\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,`,
    signature: "",
  });

  const [invoiceChangesSeperately, setInvoiceChangesSeperately] =
    useState(false);

  const [holdbackInvoiced, setHoldbackInvoiced] = useState(false);
  const [changeOrderPricingSections, setChangeOrderPricingSections] = useState(
    {},
  );

  const BASE_URL = "https://erp-production-72da01c8e651.herokuapp.com";

  const buildItemDescription = (item) => {
    console.log(item);
    let description = item.description || "";

    if (item.changeOrderNumber && item.referenceNumber) {
      const pricingSections =
        changeOrderPricingSections[item.changeOrderNumber];

      if (pricingSections) {
        const section = pricingSections.find(
          (ps) => ps.number === item.referenceNumber,
        );

        if (section && section.title) {
          const prefix = `CO #${item.changeOrderNumber} - Section ${section.number}: ${section.title}`;

          if (description && !description.startsWith("CO #")) {
            return `${prefix} - ${description}`;
          } else if (!description) {
            return prefix;
          }
        }
      }

      if (!description.startsWith("CO #")) {
        return `CO #${item.changeOrderNumber} - ${description}`;
      }
    }

    return description;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");

        const contactEmail =
          sovData.sendToEmail || project.billingContact?.email || "";
        const clientFirstName = project.billingContact?.firstName || "Client";
        const response = await axios.get(
          `${BASE_URL}/api/users/me/${decoded.email}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const displayName = response.data.success
          ? response.data.user.displayName || "User"
          : "User";

        setEmailForm((prev) => ({
          ...prev,
          to: Array.isArray(sovData.sendToEmail)
            ? sovData.sendToEmail
            : sovData.sendToEmail
              ? [sovData.sendToEmail]
              : contactEmail
                ? [contactEmail]
                : [],
          subject: `${project.projectName} - SOV Invoice#${
            sovData.qbInvoiceNumber || "Pending"
          }`,
          body: `Dear ${clientFirstName},\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,`,
          signature: displayName,
        }));
      } catch (err) {
        setAlertType("error");
        setAlertTitle("User Data Error");
        setAlertMessage("Failed to load user information.");
        setAlertOpen(true);
      }
    };
    fetchUserData();
  }, [
    project.projectName,
    sovData.sendToEmail,
    sovData.qbInvoiceNumber,
    project.billingContact?.firstName,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const projectResponse = await axios.get(
          `${BASE_URL}/api/projects/${project._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const updatedProject = projectResponse.data;
        setProject(updatedProject);

        const [
          sovResponse,
          companyProfileResponse,
          taxRateResponse,
          contactsResponse,
        ] = await Promise.all([
          fetch(`${BASE_URL}/api/sovs2/project/${project._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/company-profile/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/company-profile/tax-rate`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${BASE_URL}/api/contacts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        // After existing setters, add:
        setContacts(contactsResponse.data);

        if (!sovResponse.ok) {
          const errorData = await sovResponse.json();
          throw new Error(
            errorData.message || `SOV fetch error: ${sovResponse.status}`,
          );
        }

        const sovData = await sovResponse.json();
        const isQuickBooksConnected =
          taxRateResponse.data.isQuickBooksConnected || false;

        const [quickBooksItemsResponse, quickBooksTaxRatesResponse] =
          await Promise.all([
            isQuickBooksConnected
              ? axios.get(`${BASE_URL}/api/quickbooks/items`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
              : Promise.resolve({ data: [] }),
            isQuickBooksConnected
              ? axios.get(`${BASE_URL}/api/quickbooks/tax-codes`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
              : Promise.resolve({ data: [] }),
          ]);

        setSovs(sovData.scheduleOfValues);
        setCompanyProfile({
          ...companyProfileResponse.data,
          defaultTax: {
            taxRate: taxRateResponse.data.taxRate || "0",
            taxCodeId: taxRateResponse.data.taxCodeId || "",
            defaultItemId: taxRateResponse.data.defaultItemId || "",
          },
          plugins: {
            quickbooks: { connected: isQuickBooksConnected },
          },
        });
        setQuickBooksItems(quickBooksItemsResponse.data);
        setQuickBooksTaxRates(quickBooksTaxRatesResponse.data);
        setTaxRateData({
          taxRate: taxRateResponse.data.taxRate || "0",
          taxCodeId: taxRateResponse.data.taxCodeId || "",
        });

        let approvedCOs = [];
        const pricingSectionMap = {};

        if (updatedProject.version === 2) {
          try {
            const coResponse = await axios.get(
              `${BASE_URL}/api/changeOrders2/project/${updatedProject._id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );

            approvedCOs = coResponse.data.changeOrders
              .filter(
                (co) =>
                  co.summary?.billable === true &&
                  (co.approvalStatus === "Approved" ||
                    co.approvalStatus === "Confirmed"),
              )
              .map((co) => ({
                ...co,
                createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
              }));

            coResponse.data.changeOrders.forEach((co) => {
              if (co.pricingSections && co.pricingSections.length > 0) {
                pricingSectionMap[co.changeOrderNumber] = co.pricingSections;
              }
            });

            setChangeOrderPricingSections(pricingSectionMap);
          } catch (err) {
            console.error("Failed to load change orders:", err);
          }
        } else {
          approvedCOs = (updatedProject.changeOrders || []).filter(
            (co) =>
              co.summary?.billable === true &&
              (co.approvalStatus === "Approved" ||
                co.approvalStatus === "Confirmed"),
          );
        }

        console.log(updatedProject);

        setApprovedChangeOrders(approvedCOs);
        console.log("Billable Approved/Confirmed Change Orders:", approvedCOs);
        console.log("Pricing Sections Map:", pricingSectionMap);
      } catch (err) {
        setAlertType("error");
        setAlertTitle("Data Fetch Error");
        setAlertMessage(`Failed to load data: ${err.message}`);
        setAlertOpen(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [project._id, setProject]);

  const handleSOVChange = (e) => {
    const { name, value } = e.target;

    let finalValue = value;

    if (name === "drawNumber") {
      finalValue = value === "" ? "" : Number(value) || 1;
    }

    setSovData((prev) => {
      const next = { ...prev, [name]: finalValue };
      console.log(`Updated ${name} →`, finalValue, " | full state:", next);
      return next;
    });
  };

  const fetchPendingChangeOrders = async () => {
    try {
      const token = localStorage.getItem("token");

      let pendingCOs = [];

      if (project.version === 2) {
        const response = await axios.get(
          `${BASE_URL}/api/changeOrders2/project/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        pendingCOs = response.data.changeOrders.filter(
          (co) =>
            co.approvalStatus === "Pending" && co.summary?.billable === true,
        );
      } else {
        const projectResponse = await axios.get(
          `${BASE_URL}/api/projects/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        pendingCOs = (projectResponse.data.changeOrders || []).filter(
          (co) =>
            co.approvalStatus === "Pending" && co.summary?.billable === true,
        );
      }

      return pendingCOs;
    } catch (error) {
      console.error("Error fetching pending change orders:", error);
      return [];
    }
  };

  const renumberItems = (items) => {
    return items.map((it, idx) => ({
      ...it,
      itemNumber: idx + 1,
    }));
  };

  const handleSendEmail = () => {
    if (!userEmail) {
      setAlertType("error");
      setAlertTitle("Error");
      setAlertMessage("User email not available. Please log in again.");
      setAlertOpen(true);
      return;
    }
    generateSOVPDF(false, true);
  };

  const buildItemDescriptionForPDF = (item) => {
    if (!item.changeOrderNumber) {
      return item.description?.trim() || "";
    }

    const parts = [`CO #${item.changeOrderNumber}`];

    if (item.description?.trim()) {
      parts.push(item.description.trim());
    }

    if (item.referenceNumber?.trim()) {
      parts.push(item.referenceNumber.trim());
    }

    return parts.join(" - ").trim() || "Change Order Item";
  };

  const contactOptions = useMemo(() => {
    const accountId = project?.accountId?._id || project?.accountId;
    return contacts
      .filter((c) => !accountId || c.relatedAccount === accountId)
      .map((c) => ({
        value: c.email,
        label: `${c.firstName} ${c.lastName}${c.email ? ` (${c.email})` : ""}`,
      }));
  }, [contacts, project]);

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlertType("error");
        setAlertTitle("Invalid Input");
        setAlertMessage("Please fill in all required email fields");
        setAlertOpen(true);
        return;
      }

      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every((email) => emailRegex.test(email.trim()));
      };

      if (!validateEmails(emailForm.to)) {
        setAlertType("error");
        setAlertTitle("Invalid Email");
        setAlertMessage("Please enter valid email addresses");
        setAlertOpen(true);
        return;
      }

      if (
        !(pdfBuffer instanceof Blob) ||
        pdfBuffer.type !== "application/pdf"
      ) {
        setAlertType("error");
        setAlertTitle("Missing or Invalid Attachment");
        setAlertMessage("A valid PDF file is required");
        setAlertOpen(true);
        return;
      }

      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append(
        "to",
        Array.isArray(emailForm.to) ? emailForm.to.join(",") : emailForm.to,
      );

      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);

      formData.append(
        "files",
        pdfBuffer,
        `SOV_${project.projectNumber || "Project"}_${
          sovData.qbInvoiceNumber || new Date().toISOString().split("T")[0]
        }.pdf`,
      );

      const response = await axios.post(
        `${BASE_URL}/api/sovs2/${sovData._id}/send-email`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setShowEmailForm(false);
      setPdfBuffer(null);

      if (response.status === 200) {
        setAlertType("success");
        setAlertTitle("Success");
        setAlertMessage("Email sent successfully!");
        setAlertOpen(true);
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Error");
      setAlertMessage(
        `Failed to send email: ${err.response?.data?.message || err.message}`,
      );
      setAlertOpen(true);
    }
  };

  const handleItemChange = (index, field, value) => {
    setSovData((prev) => {
      const updatedItems = [...prev.items];

      // Prevent editing description of original contract items when using custom breakdown
      if (
        isCustomBreakdown &&
        !updatedItems[index].changeOrderNumber &&
        field === "description"
      ) {
        return prev; // Ignore change — description controlled by breakdown section
      }

      let formattedValue = value;

      // Handle percentage changes - calculate workCompletedThisPeriod
      if (field === "percentage") {
        const numValue = parseFloat(value) || 0;
        // Cap at 100%
        const cappedPercentage = Math.min(Math.max(numValue, 0), 100);
        formattedValue = cappedPercentage.toFixed(2);

        const scheduledValue =
          parseFloat(updatedItems[index].scheduledValue) || 0;
        const workCompletedPreviously =
          parseFloat(updatedItems[index].workCompletedPreviously) || 0;

        // Calculate total completed based on percentage
        const totalCompleted = (cappedPercentage / 100) * scheduledValue;

        // Calculate this period as difference
        const workCompletedThisPeriod = Math.max(
          0,
          totalCompleted - workCompletedPreviously,
        );

        updatedItems[index] = {
          ...updatedItems[index],
          percentage: formattedValue,
          workCompletedThisPeriod: workCompletedThisPeriod.toFixed(2),
          totalCompletedToDate: totalCompleted.toFixed(2),
          balanceToFinish: (scheduledValue - totalCompleted).toFixed(2),
        };

        return { ...prev, items: updatedItems };
      }

      // Handle other numeric fields
      if (["scheduledValue", "taxRate"].includes(field)) {
        const numValue = parseFloat(value) || 0;
        formattedValue = numValue.toFixed(2);
      }

      updatedItems[index] = { ...updatedItems[index], [field]: formattedValue };

      // Recalculate values when scheduled value changes
      if (field === "scheduledValue") {
        const newScheduledValue = parseFloat(formattedValue) || 0;
        const workCompletedPreviously =
          parseFloat(updatedItems[index].workCompletedPreviously) || 0;
        const workCompletedThisPeriod =
          parseFloat(updatedItems[index].workCompletedThisPeriod) || 0;
        const totalCompleted =
          workCompletedPreviously + workCompletedThisPeriod;

        updatedItems[index].totalCompletedToDate = totalCompleted.toFixed(2);
        updatedItems[index].percentage = newScheduledValue
          ? ((totalCompleted / newScheduledValue) * 100).toFixed(2)
          : "0.00";
        updatedItems[index].balanceToFinish = (
          newScheduledValue - totalCompleted
        ).toFixed(2);
      }

      return { ...prev, items: updatedItems };
    });
  };

  const calculatePreviousInvoiced = (
    description,
    excludeSovId,
    changeOrderNumber = null,
    area = null,
  ) => {
    // This function is only called from buildFinalItems for editing scenarios.
    // For new SOVs, computePreviousValues handles it.
    // Find most recent SOV by drawNumber
    const sorted = [...sovs]
      .filter((sov) => sov._id !== excludeSovId)
      .sort((a, b) => (b.drawNumber || 0) - (a.drawNumber || 0));

    if (sorted.length === 0) return 0;

    const latestSov = sorted[0];

    if (changeOrderNumber !== null) {
      const match = latestSov.items.find(
        (item) =>
          String(item.changeOrderNumber) === String(changeOrderNumber) &&
          item.description === description,
      );
      return parseFloat(match?.totalCompletedToDate || 0);
    }

    // For original items, find by description + area
    const match = latestSov.items.find((item) => {
      if (item.changeOrderNumber) return false;
      const descMatch = item.description === description;
      const areaMatch =
        area !== null ? (item.area || "").trim() === (area || "").trim() : true;
      return descMatch && areaMatch;
    });

    return parseFloat(match?.totalCompletedToDate || 0);
  };

  const removeLineItem = (index) => {
    setSovData((prev) => {
      if (prev.status === "voided") return prev;
      const updatedItems = prev.items
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, itemNumber: i + 1 }));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemUp = (index) => {
    if (index === 0 || sovData.status === "voided") return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index - 1], updatedItems[index]] = [
        updatedItems[index],
        updatedItems[index - 1],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemDown = (index) => {
    if (index === sovData.items.length - 1 || sovData.status === "voided")
      return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index], updatedItems[index + 1]] = [
        updatedItems[index + 1],
        updatedItems[index],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  const handleBreakdownItemChange = (index, field, value) => {
    setCustomBreakdownItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    setSovData((prev) => {
      const updatedItems = prev.items.map((item, i) => {
        if (i === index && !item.changeOrderNumber) {
          if (field === "description" || field === "area") {
            return {
              ...item,
              [field]:
                value ||
                (field === "description" ? `Breakdown Item ${index + 1}` : ""),
            };
          }
          if (field === "amount") {
            const scheduledValue = (parseFloat(value) || 0).toFixed(2);
            const totalCompleted =
              (parseFloat(item.workCompletedPreviously) || 0) +
              (parseFloat(item.workCompletedThisPeriod) || 0);
            return {
              ...item,
              scheduledValue,
              totalCompletedToDate: totalCompleted.toFixed(2),
              percentage: parseFloat(scheduledValue)
                ? ((totalCompleted / parseFloat(scheduledValue)) * 100).toFixed(
                    2,
                  )
                : "0.00",
              balanceToFinish: (
                parseFloat(scheduledValue) - totalCompleted
              ).toFixed(2),
            };
          }
        }
        return item;
      });

      // When editing an existing SOV, preserve the items as-is (with their correct workCompletedPreviously)
      // Only rebuild for new SOVs
      if (editingSovId) {
        // Preserve change order items and just update the original items
        const originalItems = updatedItems.filter(
          (item) => !item.changeOrderNumber,
        );
        const coItems = updatedItems.filter((item) => item.changeOrderNumber);
        return {
          ...prev,
          items: [...originalItems, ...coItems].map((item, idx) => ({
            ...item,
            itemNumber: idx + 1,
          })),
        };
      }

      return {
        ...prev,
        items: buildFinalItemsWithPrevious(
          updatedItems
            .filter((_, i) => i < customBreakdownItems.length)
            .map((item, i) => ({
              description:
                i === index && field === "description"
                  ? value
                  : item.description,
              amount:
                i === index && field === "amount" ? value : item.scheduledValue,
              area: i === index && field === "area" ? value : item.area || "",
            })),
          updatedItems,
        ),
      };
    });
  };
  const isIgnoredBreakdownItem = (description = "") => {
    return /(CN|CQ|T&M)/i.test(description);
  };

  useEffect(() => {
    if (!isCustomBreakdown) return;

    // Skip when editing — handleViewSOV already calculated correct values
    if (editingSovId) return;

    // CRITICAL FIX: Skip if items already have correct previous values
    // (i.e., handleCreateSOV already set them via computePreviousValues)
    // Only rebuild if breakdown descriptions/amounts actually changed
    // We detect this by checking if the items count matches
    const currentOriginalItems = sovData.items.filter(
      (i) => !i.changeOrderNumber,
    );
    if (currentOriginalItems.length === customBreakdownItems.length) {
      // Check if descriptions and amounts match — if so, skip rebuild
      const allMatch = customBreakdownItems.every((bi, idx) => {
        const si = currentOriginalItems[idx];
        if (!si) return false;
        return (
          si.description === (bi.description || `Breakdown Item ${idx + 1}`) &&
          si.scheduledValue === (parseFloat(bi.amount) || 0).toFixed(2)
        );
      });
      if (allMatch) return; // Items already correct, don't overwrite
    }

    // Only reach here if user actually changed breakdown items in the UI
    const rebuilt = buildFinalItemsWithPrevious(
      customBreakdownItems,
      sovData.items,
    );

    setSovData((prev) => {
      if (JSON.stringify(prev.items) === JSON.stringify(rebuilt)) return prev;
      return { ...prev, items: rebuilt };
    });
  }, [customBreakdownItems, isCustomBreakdown, editingSovId]);

  const calculateRemainingBreakdown = () => {
    const originalItemsTotal = customBreakdownItems
      .filter((item) => {
        const desc = item.description || "";
        if (isIgnoredBreakdownItem(desc)) return false;
        const isChangeOrder = approvedChangeOrders.some(
          (co) =>
            desc.includes(co.changeOrderNumber) || desc.includes(co.title),
        );
        return !isChangeOrder;
      })
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const originalContractValue =
      parseFloat(project.originalContractValue) || 0;
    return (originalContractValue - originalItemsTotal).toFixed(2);
  };

  const buildFinalItemsWithPrevious = (breakdownItems, previousItems = []) => {
    const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxCodeId ||
        quickBooksTaxRates.find((tr) =>
          tr.taxRates.some((rate) => rate.rate === 0),
        )?.id ||
        quickBooksTaxRates[0]?.id ||
        ""
      : "";
    const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxRate || "0"
      : parseFloat(taxRateData.taxRate) || 0;

    // Build raw original items from breakdown
    const rawOriginalItems = breakdownItems.map((bi, index) => {
      const existingItem = previousItems[index];
      return {
        itemNumber: index + 1,
        description: bi.description || `Breakdown Item ${index + 1}`,
        area: bi.area || existingItem?.area || "",
        taxCodeId: existingItem?.taxCodeId || defaultTaxCode,
        taxRate: existingItem?.taxRate || defaultTaxRate.toString(),
        scheduledValue: (parseFloat(bi.amount) || 0).toFixed(2),
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod:
          existingItem?.workCompletedThisPeriod || "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: (parseFloat(bi.amount) || 0).toFixed(2),
        qbItem: existingItem?.qbItem || getDefaultQbItemId(),
        changeOrderNumber: null,
      };
    });

    // Carry forward existing CO items
    const existingCoItems = previousItems
      .filter((i) => !!i.changeOrderNumber)
      .map((item) => ({
        ...item,
        workCompletedPreviously: "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: item.scheduledValue,
      }));

    // Add any new approved COs not already in the list
    const existingCoNumbers = new Set(
      existingCoItems.map((i) => String(i.changeOrderNumber)),
    );

    const newCoItems = approvedChangeOrders
      .filter((co) => !existingCoNumbers.has(String(co.changeOrderNumber)))
      .map((co) => {
        const scheduledValue =
          parseFloat(co.totalCustomerPrice || co.totalPrice) || 0;
        return {
          itemNumber: 0,
          description: co.title,
          area: "",
          changeOrderNumber: String(co.changeOrderNumber),
          changeOrderId: co._id,
          referenceNumber: co.referenceNumber || null,
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: scheduledValue.toFixed(2),
          workCompletedPreviously: "0.00",
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: "0.00",
          percentage: "0.00",
          balanceToFinish: scheduledValue.toFixed(2),
          qbItem: getDefaultQbItemId(),
        };
      });

    const rawItems = renumberItems([
      ...rawOriginalItems,
      ...existingCoItems,
      ...newCoItems,
    ]);

    // Now compute previous values using the latest SOV
    // Get the draw number for the current SOV being created/edited
    const currentDrawNumber = sovData.drawNumber || sovs.length + 1;
    return computePreviousValues(
      rawItems,
      sovs,
      editingSovId,
      currentDrawNumber,
    );
  };

  // Returns YYYY-MM-DD string in local date (ignores time & timezone offset)
  const toLocalDateString = (isoOrDate) => {
    if (!isoOrDate) return "";
    const d = new Date(isoOrDate);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // "2025-10-15"
  };

  // For display / PDF — force local calendar date, never shifted
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    // Most reliable way: split and reconstruct
    if (dateStr.includes("T")) {
      return dateStr.split("T")[0]; // just take YYYY-MM-DD part
    }
    return dateStr;
  };

  const addBreakdownItem = () => {
    setCustomBreakdownItems((prev) => [
      ...prev,
      { description: "", amount: "0.00", area: "" }, // Include area
    ]);
    setSovData((prev) => {
      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? companyProfile.defaultTaxRate ||
          quickBooksTaxRates.find((tr) =>
            tr.taxRates.some((rate) => rate.rate === 0),
          )?.id ||
          quickBooksTaxRates[0]?.id ||
          ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? ""
        : parseFloat(companyProfile?.defaultTaxRate) || 0;
      const newItem = {
        itemNumber:
          prev.items.filter((item) => !item.changeOrderNumber).length + 1,
        description: `Breakdown Item ${prev.items.length + 1}`,
        taxCodeId: defaultTaxCode,
        taxRate: defaultTaxRate.toString(),
        scheduledValue: "0.00",
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod: "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: "0.00",
        qbItem: getDefaultQbItemId(),
      };
      const updatedItems = prev.items.map((item, index) => ({
        ...item,
        itemNumber: index + 1,
      }));
      return {
        ...prev,
        items: [
          ...updatedItems.filter((item) => !item.changeOrderNumber),
          newItem,
          ...updatedItems.filter((item) => item.changeOrderNumber),
        ],
      };
    });
  };

  const removeBreakdownItem = (index) => {
    setCustomBreakdownItems((prev) => {
      const removedItem = prev[index];
      const description =
        removedItem.description || `Breakdown Item ${index + 1}`;
      const sovItem = sovData.items.find(
        (item) => item.description === description,
      );

      if (sovItem) {
        const hasProgress =
          (parseFloat(sovItem.workCompletedPreviously) || 0) > 0 ||
          (parseFloat(sovItem.workCompletedThisPeriod) || 0) > 0;
        if (hasProgress) {
          setAlertType("error");
          setAlertTitle("Cannot Remove Item");
          setAlertMessage(
            `Cannot remove "${description}" because it has recorded work completed or payments.`,
          );
          setAlertOpen(true);
          return prev;
        }
      }

      if (prev.length === 1) {
        setAlertType("error");
        setAlertTitle("Cannot Remove Item");
        setAlertMessage("At least one breakdown item is required.");
        setAlertOpen(true);
        return prev;
      }

      const updatedBreakdownItems = prev.filter((_, i) => i !== index);
      setSovData((prevSov) => {
        const updatedSovItems = prevSov.items
          .filter((item) => item.description !== description)
          .map((item, i) => ({ ...item, itemNumber: i + 1 }));
        return { ...prevSov, items: updatedSovItems };
      });
      return updatedBreakdownItems;
    });
  };

  const validateBreakdownTotal = () => {
    const filteredTotal = customBreakdownItems
      .filter((item) => {
        const desc = item.description || "";
        if (isIgnoredBreakdownItem(desc)) return false;
        const isChangeOrder = approvedChangeOrders.some(
          (co) =>
            desc.includes(co.changeOrderNumber) || desc.includes(co.title),
        );
        return !isChangeOrder;
      })
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const originalContractValue =
      parseFloat(project.originalContractValue) || 0;

    if (filteredTotal.toFixed(2) !== originalContractValue.toFixed(2)) {
      return (
        `Breakdown total ($${filteredTotal.toFixed(
          2,
        )}) must equal Original Contract Value ` +
        `($${originalContractValue.toFixed(2)}). Remaining: $${(
          originalContractValue - filteredTotal
        ).toFixed(2)}.`
      );
    }
    return null;
  };

  const generateSOVPDF = async (shouldDownload = true, sendEmail = false) => {
    if (!companyProfile) {
      setAlertType("error");
      setAlertTitle("PDF Generation Error");
      setAlertMessage("Cannot generate PDF: Company profile is not set.");
      setAlertOpen(true);
      return null;
    }

    try {
      const response = await fetch("/sov_template2.docx");
      if (!response.ok) {
        throw new Error(`Failed to load SOV template: ${response.statusText}`);
      }
      const templateArrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) {
          return null;
        }
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error("Error parsing base64 data, invalid characters");
        }
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }

      let imageBase64 = null;
      if (companyProfile?.logo) {
        if (!companyProfile.logo.startsWith("data:image")) {
          const logoResponse = await fetch(companyProfile.logo);
          if (logoResponse.ok) {
            const logoBlob = await logoResponse.blob();
            imageBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(logoBlob);
            });
          }
        } else {
          imageBase64 = companyProfile.logo;
        }
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });

      const companyName = companyProfile?.companyName || "Your Company Name";
      const companyAddress = companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
        : "N/A";

      const taxRate =
        sovData.items.length > 0
          ? companyProfile?.plugins?.quickbooks?.connected
            ? parseFloat(
                quickBooksTaxRates.find(
                  (tr) => tr.id === sovData.items[0].taxCodeId,
                )?.taxRates[0]?.rate || 0,
              )
            : parseFloat(sovData.items[0].taxRate) || 0
          : 0;

      // Fetch pending change orders from the backend
      const pendingCOs = await fetchPendingChangeOrders();

      // Prepare pending change orders data
      const pendingChangeOrders = pendingCOs.map((co) => ({
        changeOrderNumber: `TT-CQ-${co.changeOrderNumber}` || "N/A",
        reference: co.referenceNumber || "",
        title: co.title || "Untitled",
        totalPrice: formatDollarValue(
          co.totalCustomerPrice || co.totalBudgetCost || 0,
        ),
      }));

      const totalPendingCOs = pendingCOs.reduce(
        (sum, co) =>
          sum + (parseFloat(co.totalCustomerPrice || co.totalBudgetCost) || 0),
        0,
      );

      // Updated groupItemsByArea function with separate numbering for change orders
      const groupItemsByArea = (items) => {
        const areaMap = new Map();

        // 1. Group ORIGINAL CONTRACT items by area
        const originalItems = items.filter((item) => !item.changeOrderNumber);

        // Renumber original items starting from 1 (numeric)
        let originalItemNumber = 1;

        // Letter counter for subtotals
        let letterIndex = 0;

        const groupedOriginal = [];

        // Sort areas (optional - you had sorting before)
        const areaOrder = [];
        originalItems.forEach((item) => {
          const area = (item.area || "No Area Specified").trim();
          if (!areaOrder.includes(area)) {
            areaOrder.push(area);
          }
        });

        for (const areaKey of areaOrder) {
          const areaItems = originalItems.filter(
            (item) => (item.area || "No Area Specified").trim() === areaKey,
          );

          if (areaItems.length === 0) continue;

          const group = {
            areaName: areaKey,
            items: [],
            subtotal: {
              scheduledValue: 0,
              workCompletedPreviously: 0,
              workCompletedThisPeriod: 0,
              totalCompletedToDate: 0,
              balanceToFinish: 0,
            },
          };

          // Add numbered line items
          areaItems.forEach((item) => {
            group.items.push({
              in: originalItemNumber++, // numbers for items
              description: item.description?.trim() || "Unnamed Item",
              taxRate: (item.taxRate || "0") + "%",
              sv: formatDollarValue(item.scheduledValue || "0.00"),
              wcpp: formatDollarValue(item.workCompletedPreviously || "0.00"),
              wctp: formatDollarValue(item.workCompletedThisPeriod || "0.00"),
              tctd: formatDollarValue(item.totalCompletedToDate || "0.00"),
              p: item.percentage || "0.00",
              btf: formatDollarValue(item.balanceToFinish || "0.00"),
            });

            // accumulate
            group.subtotal.scheduledValue += parseFloat(
              item.scheduledValue || 0,
            );
            group.subtotal.workCompletedPreviously += parseFloat(
              item.workCompletedPreviously || 0,
            );
            group.subtotal.workCompletedThisPeriod += parseFloat(
              item.workCompletedThisPeriod || 0,
            );
            group.subtotal.totalCompletedToDate += parseFloat(
              item.totalCompletedToDate || 0,
            );
            group.subtotal.balanceToFinish += parseFloat(
              item.balanceToFinish || 0,
            );
          });

          // Now calculate percentage for this area
          const subt = group.subtotal;
          const areaPercentage =
            subt.scheduledValue > 0
              ? (
                  (subt.totalCompletedToDate / subt.scheduledValue) *
                  100
                ).toFixed(2)
              : "0.00";

          // Assign letter to the subtotal "in" field
          letterIndex++;
          const letter = String.fromCharCode(64 + letterIndex); // A, B, C, ...

          // Add subtotal fields directly to the group (with letter in 'in')
          groupedOriginal.push({
            areaName: group.areaName,
            items: group.items,
            // Subtotal row fields - now with letter
            in: letter, // ← this is what you wanted
            sbtsv: formatDollarValue(subt.scheduledValue.toFixed(2)),
            sbtwcp: formatDollarValue(subt.workCompletedPreviously.toFixed(2)),
            sbttp: formatDollarValue(subt.workCompletedThisPeriod.toFixed(2)),
            sbtc: formatDollarValue(subt.totalCompletedToDate.toFixed(2)),
            stp: areaPercentage,
            stb: formatDollarValue(subt.balanceToFinish.toFixed(2)),
          });
        }

        // ──────────────────────────────────────────────────────────────
        // CHANGE ORDERS – unchanged (still numeric 1, 2, 3...)
        // ──────────────────────────────────────────────────────────────
        const changeOrderItemsRaw = items.filter(
          (item) => !!item.changeOrderNumber,
        );

        const changeOrderItems = changeOrderItemsRaw.map((item, index) => ({
          in: index + 1,
          description: buildItemDescriptionForPDF(item),
          taxRate: (item.taxRate || "0") + "%",
          sv: formatDollarValue(item.scheduledValue || "0.00"),
          wcpp: formatDollarValue(item.workCompletedPreviously || "0.00"),
          wctp: formatDollarValue(item.workCompletedThisPeriod || "0.00"),
          tctd: formatDollarValue(item.totalCompletedToDate || "0.00"),
          p: item.percentage || "0.00",
          btf: formatDollarValue(item.balanceToFinish || "0.00"),
        }));

        const coSubtotal = {
          scheduledValue: changeOrderItemsRaw.reduce(
            (sum, i) => sum + (parseFloat(i.scheduledValue) || 0),
            0,
          ),
          workCompletedPreviously: changeOrderItemsRaw.reduce(
            (sum, i) => sum + (parseFloat(i.workCompletedPreviously) || 0),
            0,
          ),
          workCompletedThisPeriod: changeOrderItemsRaw.reduce(
            (sum, i) => sum + (parseFloat(i.workCompletedThisPeriod) || 0),
            0,
          ),
          totalCompletedToDate: changeOrderItemsRaw.reduce(
            (sum, i) => sum + (parseFloat(i.totalCompletedToDate) || 0),
            0,
          ),
          balanceToFinish: changeOrderItemsRaw.reduce(
            (sum, i) => sum + (parseFloat(i.balanceToFinish) || 0),
            0,
          ),
        };

        const coPercentage =
          coSubtotal.scheduledValue > 0
            ? (
                (coSubtotal.totalCompletedToDate / coSubtotal.scheduledValue) *
                100
              ).toFixed(2)
            : "0.00";

        // Base contract totals (for overall base contract subtotal row)
        const baseContractTotals = {
          scheduledValue: originalItems.reduce(
            (sum, i) => sum + (parseFloat(i.scheduledValue) || 0),
            0,
          ),
          workCompletedPreviously: originalItems.reduce(
            (sum, i) => sum + (parseFloat(i.workCompletedPreviously) || 0),
            0,
          ),
          workCompletedThisPeriod: originalItems.reduce(
            (sum, i) => sum + (parseFloat(i.workCompletedThisPeriod) || 0),
            0,
          ),
          totalCompletedToDate: originalItems.reduce(
            (sum, i) => sum + (parseFloat(i.totalCompletedToDate) || 0),
            0,
          ),
          balanceToFinish: originalItems.reduce(
            (sum, i) => sum + (parseFloat(i.balanceToFinish) || 0),
            0,
          ),
        };

        const baseContractPercentage =
          baseContractTotals.scheduledValue > 0
            ? (
                (baseContractTotals.totalCompletedToDate /
                  baseContractTotals.scheduledValue) *
                100
              ).toFixed(2)
            : "0.00";

        return {
          originalAreas: groupedOriginal, // each has .items + .in (letter) + subtotal fields
          changeOrders: changeOrderItems,
          hasChangeOrders: changeOrderItems.length > 0,
          baseContractSubtotals: {
            sbtsv: formatDollarValue(
              baseContractTotals.scheduledValue.toFixed(2),
            ),
            sbtwcp: formatDollarValue(
              baseContractTotals.workCompletedPreviously.toFixed(2),
            ),
            sbttp: formatDollarValue(
              baseContractTotals.workCompletedThisPeriod.toFixed(2),
            ),
            sbtc: formatDollarValue(
              baseContractTotals.totalCompletedToDate.toFixed(2),
            ),
            stp: baseContractPercentage,
            stb: formatDollarValue(
              baseContractTotals.balanceToFinish.toFixed(2),
            ),
          },
          changeOrderSubtotals: {
            sbtsv: formatDollarValue(coSubtotal.scheduledValue.toFixed(2)),
            sbtwcp: formatDollarValue(
              coSubtotal.workCompletedPreviously.toFixed(2),
            ),
            sbttp: formatDollarValue(
              coSubtotal.workCompletedThisPeriod.toFixed(2),
            ),
            sbtc: formatDollarValue(coSubtotal.totalCompletedToDate.toFixed(2)),
            stp: coPercentage,
            stb: formatDollarValue(coSubtotal.balanceToFinish.toFixed(2)),
          },
          rawBaseContract: baseContractTotals,
          rawChangeOrder: coSubtotal,
        };
      };

      const groupedData = groupItemsByArea(sovData.items);

      const totalScheduledValue = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.scheduledValue) || 0),
        0,
      );
      const totalWorkCompletedPreviously = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.workCompletedPreviously) || 0),
        0,
      );
      const thisPeriodSubtotal = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
        0,
      );
      const totalCompletedToDate = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.totalCompletedToDate) || 0),
        0,
      );
      const totalBalanceToFinish = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.balanceToFinish) || 0),
        0,
      );
      const totalPercentage = totalScheduledValue
        ? ((totalCompletedToDate / totalScheduledValue) * 100).toFixed(2)
        : "0.00";

      // ───────────────────────────────────────────────────────────────
      // NEW: Add flat, top-level subtotal objects (non-looped)
      // You can now use these directly in the template outside loops
      // ───────────────────────────────────────────────────────────────
      const originalSubtotals = groupedData.baseContractSubtotals;
      const changeOrderSubtotalsFlat = groupedData.changeOrderSubtotals;

      const grandSubtotals = {
        sbtsv: formatDollarValue(
          (
            parseFloat(groupedData.rawBaseContract.scheduledValue || 0) +
            parseFloat(groupedData.rawChangeOrder.scheduledValue || 0)
          ).toFixed(2),
        ),
        sbtwcp: formatDollarValue(
          (
            parseFloat(
              groupedData.rawBaseContract.workCompletedPreviously || 0,
            ) +
            parseFloat(groupedData.rawChangeOrder.workCompletedPreviously || 0)
          ).toFixed(2),
        ),
        sbttp: formatDollarValue(
          (
            parseFloat(
              groupedData.rawBaseContract.workCompletedThisPeriod || 0,
            ) +
            parseFloat(groupedData.rawChangeOrder.workCompletedThisPeriod || 0)
          ).toFixed(2),
        ),
        sbtc: formatDollarValue(
          (
            parseFloat(groupedData.rawBaseContract.totalCompletedToDate || 0) +
            parseFloat(groupedData.rawChangeOrder.totalCompletedToDate || 0)
          ).toFixed(2),
        ),
        stp: totalPercentage,
        stb: formatDollarValue(
          (
            parseFloat(groupedData.rawBaseContract.balanceToFinish || 0) +
            parseFloat(groupedData.rawChangeOrder.balanceToFinish || 0)
          ).toFixed(2),
        ),
      };

      // Calculate main table totals

      const hst = sovData.items.reduce((sum, item) => {
        const itemTaxRate = companyProfile?.plugins?.quickbooks?.connected
          ? parseFloat(
              quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)
                ?.taxRates[0]?.rate || 0,
            )
          : parseFloat(item.taxRate) || 0;
        return (
          sum +
          (parseFloat(item.workCompletedThisPeriod) || 0) * (itemTaxRate / 100)
        );
      }, 0);

      const subTotalWithTax = thisPeriodSubtotal + hst;
      const currentHoldback = sovData.withHoldback ? subTotalWithTax * 0.1 : 0;
      const totalProjectHoldback = sovData.withHoldback
        ? totalCompletedToDate * (1 + taxRate / 100) * 0.1
        : 0;
      const remainingBalance = subTotalWithTax - currentHoldback;

      // NEW SUMMARY CALCULATIONS
      const baseContractDrawSubtotal =
        groupedData.rawBaseContract.workCompletedThisPeriod;
      const changeOrderDrawSubtotal =
        groupedData.rawChangeOrder.workCompletedThisPeriod;
      const totalDrawAmount =
        baseContractDrawSubtotal + changeOrderDrawSubtotal;
      const holdbackAmount = sovData.withHoldback ? totalDrawAmount * 0.1 : 0;
      const netAmount = totalDrawAmount - holdbackAmount;
      const totalTax = sovData.items.reduce((sum, item) => {
        const itemTaxRate = companyProfile?.plugins?.quickbooks?.connected
          ? parseFloat(
              quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)
                ?.taxRates[0]?.rate || 0,
            )
          : parseFloat(item.taxRate) || 0;
        return (
          sum +
          (parseFloat(item.workCompletedThisPeriod) || 0) * (itemTaxRate / 100)
        );
      }, 0);
      const amountPayable = netAmount + totalTax;

      const allInvoiceComponents = sovs.flatMap((sov) =>
        (sov.invoiceComponents || []).map((component) => ({
          ...component,
          sovId: sov._id,
          sovInvoiceDate: sov.invoiceDate,
          sovDueDate: sov.dueDate,
        })),
      );

      allInvoiceComponents.sort((a, b) => {
        const dateA = new Date(a.invoiceDate || a.sovInvoiceDate || 0);
        const dateB = new Date(b.invoiceDate || b.sovInvoiceDate || 0);
        return dateA - dateB;
      });

      // Enhanced invoice overview with all required fields
      const invoiceOverview = allInvoiceComponents.map((invoice) => {
        const amount = parseFloat(invoice.amount) || 0;
        const holdback = parseFloat(invoice.holdback) || 0;
        const tax = parseFloat(invoice.tax) || 0;
        const total = parseFloat(invoice.total);
        const amountPaid = parseFloat(invoice.amountPaid) || 0;

        return {
          component:
            invoice.component === "original_contract"
              ? "Original Contract"
              : invoice.component === "change_order"
                ? `CO #${invoice.changeOrderNumber}`
                : "Holdback",
          qbInvoiceNumber: invoice.qbInvoiceNumber || "N/A",
          invoiceDate: formatLocalDateNice(
            invoice.invoiceDate || invoice.sovInvoiceDate,
          ),
          amount: formatDollarValue(amount),
          holdback: formatDollarValue(holdback),
          billing: formatDollarValue(amount), // Billing = Amount before holdback
          tax: formatDollarValue(tax),
          total: formatDollarValue(total),
          amountPaid: formatDollarValue(amountPaid),
          dueDate: invoice.dueDate
            ? new Date(invoice.dueDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            : invoice.sovDueDate
              ? new Date(invoice.sovDueDate).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              : "N/A",
        };
      });

      // Calculate invoice overview totals from ALL invoices
      const totalInvoiceAmount = allInvoiceComponents.reduce(
        (sum, inv) => sum + (parseFloat(inv.amount) || 0),
        0,
      );
      const totalInvoiceHoldback = allInvoiceComponents.reduce(
        (sum, inv) => sum + (parseFloat(inv.holdback) || 0),
        0,
      );
      const totalInvoiceBilling = totalInvoiceAmount; // Billing = Amount
      const totalInvoiceTax = allInvoiceComponents.reduce(
        (sum, inv) => sum + (parseFloat(inv.tax) || 0),
        0,
      );
      const grandTotal = allInvoiceComponents.reduce(
        (sum, inv) => sum + (parseFloat(inv.total) || 0),
        0,
      );
      const totalInvoicePaid = allInvoiceComponents.reduce(
        (sum, inv) => sum + (parseFloat(inv.amountPaid) || 0),
        0,
      );

      const data = {
        companyName,
        companyAddress,
        AccountName:
          sovData.accountName || project.accountId?.companyName || "N/A",
        AccountAddress:
          sovData.accountAddress ||
          getAddressString(project.accountId?.address || {}),
        image: imageBase64,
        billTo: sovData.billTo,
        projectAddress: sovData.job,
        projectName: sovData.projectName || project.projectName || "N/A",
        originalContractValue: formatDollarValue(
          project.originalContractValue || 0,
        ),

        // NEW HEADER FIELDS
        poNumber: sovData.poNumber || "N/A",
        drawNumber: sovData.drawNumber || 1,
        date1: formatDateForDisplay(sovData.workCompletedFrom),
        date2: formatDateForDisplay(sovData.workCompletedTo),

        // Grouped structure with subtotals
        originalAreas: groupedData.originalAreas,
        changeOrders: groupedData.changeOrders,
        hasChangeOrders: groupedData.hasChangeOrders,

        // Base contract subtotals (for base contract table)
        baseContractSubtotals: groupedData.baseContractSubtotals,

        // Change order subtotals (for change order table)
        changeOrderSubtotals: groupedData.changeOrderSubtotals,

        // ── NEW: flat subtotal objects you can target outside loops ──
        originalSubtotals,
        changeOrderSubtotalsFlat,
        grandSubtotals,

        totalInvoiced: formatDollarValue(calculateTotalInvoiced()),
        qbInvoiceNumber: sovData.qbInvoiceNumber || "N/A",
        t: taxRate + "%",

        // Grand totals for entire SOV (both tables combined)
        tsv: formatDollarValue(totalScheduledValue),
        twcp: formatDollarValue(totalWorkCompletedPreviously),
        twctp: formatDollarValue(thisPeriodSubtotal),
        tctd: formatDollarValue(totalCompletedToDate),
        tp: totalPercentage,
        tbf: formatDollarValue(totalBalanceToFinish),

        // NEW SUMMARY SECTION
        bcdst: formatDollarValue(baseContractDrawSubtotal),
        codst: formatDollarValue(changeOrderDrawSubtotal),
        tda: formatDollarValue(totalDrawAmount),
        tholdback: formatDollarValue(holdbackAmount),
        neta: formatDollarValue(netAmount),
        totalTax: formatDollarValue(totalTax),
        amountPayable: formatDollarValue(amountPayable),

        // Legacy fields (for backward compatibility)
        thisPeriodSubtotal: formatDollarValue(thisPeriodSubtotal),
        hst: formatDollarValue(hst),
        subTotal: formatDollarValue(subTotalWithTax),
        currentHoldback: formatDollarValue(currentHoldback),
        totalProjectHoldback: formatDollarValue(totalProjectHoldback),
        remainingBalance: formatDollarValue(remainingBalance),

        // Pending change orders table
        pendingChangeOrders,
        totalPendingCOs: formatDollarValue(totalPendingCOs),
        hasPendingChangeOrders: pendingChangeOrders.length > 0,

        // Enhanced invoice overview table
        invoiceOverview,
        totalInvoiceBilling: formatDollarValue(totalInvoiceAmount),
        totalInvoiceHoldback: formatDollarValue(totalInvoiceHoldback),
        totalInvoiceTax: formatDollarValue(totalInvoiceTax),
        grandTotal: formatDollarValue(grandTotal),
        totalInvoicePaid: formatDollarValue(totalInvoicePaid),
        hasInvoiceComponents: invoiceOverview.length > 0,

        invoiceDate: formatDateForDisplay(sovData.invoiceDate),
        dueDate: formatDateForDisplay(sovData.dueDate),

        specialInstructions: sovData.specialInstructions || "",
      };

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append(
        "docx",
        docxBlob,
        `SOV_${project.projectName}_${sovData.qbInvoiceNumber || "100"}.docx`,
      );
      formData.append("documentType", "SOV");
      formData.append("identifier", project._id || "default");

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        `${BASE_URL}/api/upload/convert-to-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });

        if (sendEmail) {
          setPdfBuffer(pdfBlob);
          setShowEmailForm(true);
        } else if (shouldDownload) {
          saveAs(
            pdfBlob,
            `SOV Draw ${sovData.drawNumber || ""} ${project.projectName || "Project"} ${sovData.invoiceDate || "undated"}.pdf`,
          );
        }

        return pdfBlob;
      } else {
        const textResponse = await pdfResponse.data.text();
        throw new Error(
          JSON.parse(textResponse)?.message || "Unknown error from server",
        );
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("PDF Generation Error");
      setAlertMessage("Failed to generate SOV PDF: " + err.message);
      setAlertOpen(true);
      return null;
    }
  };

  const generateQBInvoice = async (saveAfter = false) => {
    if (isGeneratingInvoice) return;
    setIsGeneratingInvoice(true);

    const getTaxRateForItem = (item) => {
      if (companyProfile?.plugins?.quickbooks?.connected) {
        const taxCode = quickBooksTaxRates.find(
          (tr) => tr.id === item.taxCodeId,
        );
        return parseFloat(taxCode?.taxRates?.[0]?.rate) || 0;
      }
      return parseFloat(item.taxRate) || 0;
    };

    try {
      const token = localStorage.getItem("token");
      const billingContact = project?.billingContact || {};
      const billingAccount = project?.accountId || {};

      const customer = {
        email: billingContact.email || "unknown@example.com",
        firstName: billingContact.firstName || "Unknown",
        lastName: billingContact.lastName || "Customer",
        companyName: billingAccount.companyName || "Unnamed Company",
        address: {
          street: billingAccount.address?.street || "",
          city: billingAccount.address?.city || "",
          state: billingAccount.address?.state || "",
          zip: billingAccount.address?.postalCode || "",
          country: billingAccount.address?.country || "",
        },
      };

      const lineItemsByComponent = {};

      const addToComponent = (
        componentKey,
        componentType,
        items,
        changeOrderNumber = null,
        changeOrderId = null,
      ) => {
        if (!lineItemsByComponent[componentKey]) {
          lineItemsByComponent[componentKey] = {
            component: componentType,
            lineItems: [],
            amount: "0.00",
            memo: "",
            changeOrderNumber,
            changeOrderId,
          };
        }
        items.forEach((item) =>
          lineItemsByComponent[componentKey].lineItems.push(item),
        );
        const total = lineItemsByComponent[componentKey].lineItems.reduce(
          (sum, item) => sum + parseFloat(item.amount || 0),
          0,
        );
        lineItemsByComponent[componentKey].amount = total.toFixed(2);
      };

      if (invoiceChangesSeperately) {
        const ocItems = sovData.items
          .filter(
            (item) =>
              !item.changeOrderNumber &&
              parseFloat(item.workCompletedThisPeriod || 0) > 0,
          )
          .map((item) => ({
            qbItem: getDefaultQbItemId(),
            description: buildItemDescription(item),
            amount: parseFloat(item.workCompletedThisPeriod || 0),
            taxCodeId: item.taxCodeId,
            taxRate: getTaxRateForItem(item),
          }));
        if (ocItems.length > 0) {
          addToComponent("original_contract", "original_contract", ocItems);
          lineItemsByComponent["original_contract"].memo =
            "Original Contract Work Completed This Period";
          lineItemsByComponent["original_contract"].withHoldback =
            sovData.withHoldback;
        }

        const coGroups = {};
        sovData.items
          .filter(
            (item) =>
              item.changeOrderNumber &&
              parseFloat(item.workCompletedThisPeriod || 0) > 0,
          )
          .forEach((item) => {
            const coNum = item.changeOrderNumber;
            if (!coGroups[coNum]) {
              coGroups[coNum] = [];
              coGroups[`_ref_${coNum}`] = item.referenceNumber || null;
            }
            coGroups[coNum].push({
              qbItem: getDefaultQbItemId(),
              description: buildItemDescription(item),
              amount: parseFloat(item.workCompletedThisPeriod || 0),
              taxCodeId: item.taxCodeId,
              taxRate: getTaxRateForItem(item),
              changeOrderId: item.changeOrderId,
              referenceNumber: item.referenceNumber,
            });
          });

        Object.entries(coGroups)
          .filter(([key]) => !key.startsWith("_ref_"))
          .forEach(([coNum, items]) => {
            const componentKey = `change_order_${coNum}`;
            const co = approvedChangeOrders.find(
              (c) => String(c.changeOrderNumber) === coNum,
            );
            addToComponent(
              componentKey,
              "change_order",
              items,
              coNum,
              items[0]?.changeOrderId,
            );
            lineItemsByComponent[componentKey].memo =
              co?.title || `Change Order #${coNum}`;
            lineItemsByComponent[componentKey].withHoldback =
              sovData.withHoldback;
            lineItemsByComponent[componentKey].referenceNumber =
              coGroups[`_ref_${coNum}`] || items[0]?.referenceNumber || null;
          });

        if (
          holdbackInvoiced &&
          parseFloat(sovData.holdbackInvoiceAmount || 0) > 0
        ) {
          const holdbackItem = quickBooksItems.find((i) =>
            i.name.toLowerCase().includes("holdback"),
          ) || { id: quickBooksItems[0]?.id || "" };
          addToComponent("holdback_release", "holdback", [
            {
              qbItemId: holdbackItem.id,
              description: "Holdback Release",
              amount: parseFloat(sovData.holdbackInvoiceAmount),
              taxCodeId: "NON",
            },
          ]);
          lineItemsByComponent["holdback_release"].memo =
            "Previous Holdback Release";
          lineItemsByComponent["holdback_release"].withHoldback = false;
        }
      } else {
        const allItems = sovData.items
          .filter((item) => parseFloat(item.workCompletedThisPeriod || 0) !== 0)
          .map((item) => ({
            qbItem: getDefaultQbItemId(),
            description: buildItemDescription(item),
            amount: parseFloat(item.workCompletedThisPeriod || 0),
            taxCodeId: item.taxCodeId,
            taxRate: getTaxRateForItem(item),
          }));
        if (allItems.length > 0) {
          addToComponent("combined", "original_contract", allItems);
          lineItemsByComponent["combined"].memo = "Work Completed This Period";
          lineItemsByComponent["combined"].withHoldback = sovData.withHoldback;
        }
      }

      // Add custom field values to every component
      const projectFullAddress =
        sovData.job || getAddressString(project.address) || "N/A";
      Object.values(lineItemsByComponent).forEach((comp) => {
        comp.projectAddress = projectFullAddress;
        comp.workCompletedFrom = sovData.workCompletedFrom || "";
        comp.workCompletedTo = sovData.workCompletedTo || "";
        comp.poNumber = sovData.poNumber || "";
        comp.invoiceDate = sovData.invoiceDate || "";
        comp.dueDate = sovData.dueDate || "";
      });

      // Determine if this is an UPDATE or CREATE
      const hasExistingInvoices =
        sovData.invoiceComponents &&
        sovData.invoiceComponents.length > 0 &&
        sovData.invoiceComponents.some(
          (ic) => ic.qbInvoiceId && ic.status !== "voided",
        );

      const invoiceData = {
        customer,
        sovId: sovData._id,
        invoiceDate: sovData.invoiceDate,
        dueDate: sovData.dueDate,
        invoiceChangesSeperately,
        lineItemsByComponent,
      };

      let response;

      if (hasExistingInvoices) {
        invoiceData.existingInvoiceComponents = sovData.invoiceComponents
          .filter((ic) => ic.qbInvoiceId && ic.status !== "voided")
          .map((ic) => ({
            component: ic.component,
            qbInvoiceId: ic.qbInvoiceId,
            qbInvoiceNumber: ic.qbInvoiceNumber,
            changeOrderNumber: ic.changeOrderNumber || null,
            amountPaid: ic.amountPaid || "0.00",
          }));

        response = await axios.put(
          `${BASE_URL}/api/quickbooks/invoicesV2`,
          invoiceData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/api/quickbooks/invoicesV2`,
          invoiceData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      const { invoiceComponents, pdfs, message } = response.data;

      // Merge SOV PDF + QB invoice PDFs into single download
      if (pdfs && pdfs.length > 0) {
        const mergedPdf = await PDFDocument.create();

        // QB invoices first
        for (const pdfData of pdfs) {
          const pdfBytes = Uint8Array.from(atob(pdfData.pdf), (c) =>
            c.charCodeAt(0),
          );
          const doc = await PDFDocument.load(pdfBytes);
          const pages = await mergedPdf.copyPages(doc, doc.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
        }

        // SOV after
        const sovPdfBlob = await generateSOVPDF(false, false);
        if (sovPdfBlob) {
          const sovPdfBytes = new Uint8Array(await sovPdfBlob.arrayBuffer());
          const sovDoc = await PDFDocument.load(sovPdfBytes);
          const sovPages = await mergedPdf.copyPages(
            sovDoc,
            sovDoc.getPageIndices(),
          );
          sovPages.forEach((page) => mergedPdf.addPage(page));
        }
      }

      // Update local state with new invoice components
      const updatedSovData = {
        ...sovData,
        invoiceComponents,
        generated: true,
        status: "generated",
        isCustomBreakdown,
        invoiceChangesSeperately,
        totalInvoiced: calculateTotalInvoiced(),
      };

      setSovData((prev) => ({
        ...prev,
        invoiceComponents,
        generated: true,
        status: "generated",
      }));

      // If saveAfter flag is set, also persist SOV to backend
      // Only show alert if NOT doing a save+download flow (caller will handle UX)
      if (!saveAfter) {
        setAlertType("success");
        setAlertTitle("Success");
        setAlertMessage(
          message || "QuickBooks invoices processed successfully!",
        );
        setAlertOpen(true);
      }
      return true;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "An unexpected error occurred.";

      if (err.response?.status === 409) {
        setAlertType("error");
        setAlertTitle("Stale Invoice");
        setAlertMessage(
          "The invoice was modified in QuickBooks. Please try again.",
        );
        setAlertOpen(true);
      } else {
        setAlertType("error");
        setAlertTitle("QuickBooks Invoice Error");
        setAlertMessage(errorMessage);
        setAlertOpen(true);
      }
    } finally {
      setIsGeneratingInvoice(false);
    }
  };
  const formatLocalDateNice = (isoOrDate) => {
    if (!isoOrDate) return "N/A";

    // If it's already a YYYY-MM-DD string, use it directly
    if (
      typeof isoOrDate === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)
    ) {
      const [year, month, day] = isoOrDate.split("-").map(Number);
      const d = new Date(year, month - 1, day); // local date, no time → no offset issues
      return d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    // Otherwise treat as ISO / Date object, but force to local YYYY-MM-DD first
    const d = new Date(isoOrDate);
    if (isNaN(d.getTime())) return "Invalid date";

    // Extract local year/month/day (ignores time portion and timezone shift)
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();

    const safeDate = new Date(year, month, day); // midnight local time
    return safeDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDefaultQbItemId = () => {
    if (!companyProfile?.plugins?.quickbooks?.connected) return "";

    // Priority 1: company profile setting (recommended way)
    if (companyProfile?.defaultTax?.defaultItemId) {
      return companyProfile.defaultTax.defaultItemId;
    }

    // Priority 2: first available QuickBooks item
    if (quickBooksItems?.length > 0) {
      return quickBooksItems[0].id;
    }

    // Last resort — empty (but log warning in dev)
    if (process.env.NODE_ENV !== "production") {
      console.warn("No default QuickBooks item could be determined");
    }
    return "";
  };

  const voidQBInvoice = async () => {
    if (!sovData.qbInvoiceId) {
      setAlertType("error");
      setAlertTitle("Void Invoice Error");
      setAlertMessage("No QuickBooks invoice to void.");
      setAlertOpen(true);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BASE_URL}/api/quickbooks/invoices/${sovData.qbInvoiceId}/void`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSovData((prev) => ({
        ...prev,
        status: "voided",
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        sentAt: null,
      }));
      await saveSOV({
        ...sovData,
        status: "voided",
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        totalInvoiced: "0.00",
        sentAt: null,
      });

      setAlertType("success");
      setAlertTitle("Success");
      setAlertMessage("QuickBooks invoice voided successfully!");
      setAlertOpen(true);
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Void Invoice Error");
      setAlertMessage("Failed to void QuickBooks invoice: " + err.message);
      setAlertOpen(true);
    }
  };

  const sendQBInvoice = async () => {
    if (!sovData.qbInvoiceId) {
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage(
        "No QuickBooks invoice to send. Please generate one first.",
      );
      setAlertOpen(true);
      return;
    }

    if (sovData.sentAt) {
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage("This invoice has already been sent.");
      setAlertOpen(true);
      return;
    }

    if (isSendingInvoice) return;

    setIsSendingInvoice(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        sovId: sovData._id,
      };
      if (sovData.sendToEmail) {
        payload.sendTo = sovData.sendToEmail;
      }

      const response = await axios.post(
        `${BASE_URL}/api/quickbooks/invoices/${sovData.qbInvoiceId}/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSovData((prev) => ({
        ...prev,
        status: "sent",
        sentAt: new Date(),
      }));

      await saveSOV({
        ...sovData,
        status: "sent",
        sentAt: new Date(),
      });

      setAlertType("success");
      setAlertTitle("Success");
      setAlertMessage("QuickBooks invoice sent successfully!");
      setAlertOpen(true);
    } catch (err) {
      let errorMessage =
        err.response?.data?.message || "Failed to send QuickBooks invoice";
      if (err.response?.data?.error === "Stale Object Error") {
        errorMessage = "The invoice was modified elsewhere. Please try again.";
      }
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage(errorMessage);
      setAlertOpen(true);
    } finally {
      setIsSendingInvoice(false);
    }
  };

  /**
   * Given a target SOV's items array and the full sovs list,
   * compute workCompletedPreviously for each item by finding
   * the same item (by position) in the most recent prior SOV.
   *
   * "Same item" = same index position among items with the same
   * component type (original vs change-order).
   */
  const computePreviousValues = (
    targetItems,
    allSovs,
    currentSovId,
    currentDrawNumber,
  ) => {
    // Find the most recent SOV before this one by drawNumber
    const previousSov = allSovs
      .filter(
        (s) =>
          s._id !== currentSovId && (s.drawNumber || 0) < currentDrawNumber,
      )
      .sort((a, b) => (b.drawNumber || 0) - (a.drawNumber || 0))[0];

    if (!previousSov) {
      // No prior SOV exists — everything is 0
      return targetItems.map((item) => ({
        ...item,
        workCompletedPreviously: "0.00",
      }));
    }

    const prevOriginalItems = previousSov.items.filter(
      (i) => !i.changeOrderNumber,
    );
    const prevCoItems = previousSov.items.filter((i) => !!i.changeOrderNumber);

    let originalIndex = 0;

    return targetItems.map((item) => {
      let prevItem = null;

      if (!item.changeOrderNumber) {
        // Original contract item — match by position in the original items list
        prevItem = prevOriginalItems[originalIndex] || null;
        originalIndex++;
      } else {
        // Change order item — match by changeOrderNumber (unique)
        prevItem = prevCoItems.find(
          (pi) =>
            String(pi.changeOrderNumber) === String(item.changeOrderNumber),
        );
      }

      const prevCompleted = parseFloat(prevItem?.totalCompletedToDate || 0);
      const workThisPeriod = parseFloat(item.workCompletedThisPeriod || 0);
      const totalCompleted = prevCompleted + workThisPeriod;
      const scheduledValue = parseFloat(item.scheduledValue || 0);

      return {
        ...item,
        workCompletedPreviously: prevCompleted.toFixed(2),
        totalCompletedToDate: totalCompleted.toFixed(2),
        percentage: scheduledValue
          ? ((totalCompleted / Math.abs(scheduledValue)) * 100).toFixed(2)
          : "0.00",
        balanceToFinish: (scheduledValue - totalCompleted).toFixed(2),
      };
    });
  };

  const handleViewSOV = (sov) => {
    const originalContractItems = sov.items.filter(
      (item) => !item.changeOrderNumber,
    );
    const isSovCustomBreakdown =
      originalContractItems.length > 1 || sov.isCustomBreakdown;
    setIsCustomBreakdown(isSovCustomBreakdown);
    setCustomBreakdownItems(
      isSovCustomBreakdown
        ? originalContractItems.map((item) => ({
            description: item.description,
            amount: item.scheduledValue,
            area: item.area || "",
          }))
        : [{ description: "", amount: "0.00" }],
    );
    setIsBreakdownExpanded(false);

    setInvoiceChangesSeperately(sov.invoiceChangesSeperately || false);
    setHoldbackInvoiced(sov.holdbackInvoiced || false);

    // Use positional matching to compute correct previous values
    const updatedItems = computePreviousValues(
      sov.items.map((item, index) => ({
        ...item,
        itemNumber: index + 1,
        workCompletedThisPeriod: (
          parseFloat(item.workCompletedThisPeriod) || 0
        ).toFixed(2),
      })),
      sovs,
      sov._id,
      sov.drawNumber || 0,
    );

    setSovData({
      ...sov,
      items: updatedItems,
      accountName: sov.accountName || project.accountId?.companyName || "N/A",
      accountAddress:
        sov.accountAddress ||
        getAddressString(project.accountId?.address || {}),
      projectName: sov.projectName || project.projectName || "N/A",
      sendToEmail: Array.isArray(sov.sendToEmail)
        ? sov.sendToEmail
        : sov.sendToEmail
          ? [sov.sendToEmail]
          : project.billingContact?.email
            ? [project.billingContact.email]
            : [],
      specialInstructions: sov.specialInstructions || "",
      poNumber: sov.poNumber || "",
      drawNumber: sov.drawNumber || 1,
      workCompletedFrom: sov.workCompletedFrom || "",
      workCompletedTo: sov.workCompletedTo || "",
    });
    setEditingSovId(sov._id);
    setShowCreateForm(true);
  };

  const downloadSOVWithInvoices = async () => {
    try {
      setIsDownloading(true);

      // 1. Generate SOV PDF
      const sovPdfBlob = await generateSOVPDF(false, false);
      if (!sovPdfBlob) {
        setAlertType("error");
        setAlertTitle("Error");
        setAlertMessage("Failed to generate SOV PDF");
        setAlertOpen(true);
        return;
      }

      // 2. Check if we have any generated QB invoices
      const invoiceComponents = sovData.invoiceComponents?.filter(
        (comp) => comp.qbInvoiceId && comp.status !== "voided",
      );

      if (!invoiceComponents || invoiceComponents.length === 0) {
        // No QB invoices → just download plain SOV
        saveAs(
          sovPdfBlob,
          `SOV Draw ${sovData.drawNumber || ""} ${sovData.invoiceDate || "undated"} ${project.projectName || "Project"}.pdf`,
        );
        return;
      }

      // 3. Has QB invoices → fetch them and merge
      const token = localStorage.getItem("token");
      const invoiceIds = invoiceComponents.map((comp) => ({
        qbInvoiceId: comp.qbInvoiceId,
        qbInvoiceNumber: comp.qbInvoiceNumber,
        component: comp.component,
      }));

      const response = await axios.post(
        `${BASE_URL}/api/quickbooks/invoices/pdfs`,
        { invoiceIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const qbPdfs = response.data.pdfs?.filter((p) => p.pdf !== null) || [];

      if (qbPdfs.length === 0) {
        saveAs(
          sovPdfBlob,
          `SOV Draw ${sovData.drawNumber || ""} ${sovData.invoiceDate || "undated"} ${project.projectName || "Project"}.pdf`,
        );
        return;
      }

      // Merge everything — QB invoices first, then SOV
      const mergedPdf = await PDFDocument.create();

      for (const qbPdf of qbPdfs) {
        const pdfBytes = Uint8Array.from(atob(qbPdf.pdf), (c) =>
          c.charCodeAt(0),
        );
        const qbDoc = await PDFDocument.load(pdfBytes);
        const qbPages = await mergedPdf.copyPages(
          qbDoc,
          qbDoc.getPageIndices(),
        );
        qbPages.forEach((page) => mergedPdf.addPage(page));
      }

      const sovBytes = new Uint8Array(await sovPdfBlob.arrayBuffer());
      const sovDoc = await PDFDocument.load(sovBytes);
      const sovPages = await mergedPdf.copyPages(
        sovDoc,
        sovDoc.getPageIndices(),
      );
      sovPages.forEach((page) => mergedPdf.addPage(page));

      const mergedBytes = await mergedPdf.save();
      const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });

      const invoiceNumbers = qbPdfs.map((p) => p.qbInvoiceNumber).join("_");
      saveAs(
        mergedBlob,
        `INV ${invoiceNumbers} ${sovData.invoiceDate || "undated"} #${sovData.drawNumber || ""} ${project.projectName || "Project"}.pdf`,
      );
    } catch (error) {
      console.error("Download error:", error);
      setAlertType("error");
      setAlertTitle("Download Error");
      setAlertMessage(
        "Failed to download: " +
          (error.response?.data?.message || error.message),
      );
      setAlertOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (isCustomBreakdown) {
      const breakdownError = validateBreakdownTotal();
      if (breakdownError) {
        setAlertType("error");
        setAlertTitle("Breakdown Validation Error");
        setAlertMessage(breakdownError);
        setAlertOpen(true);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const totalInvoiced = calculateTotalInvoiced();

      const dataToSubmit = {
        ...sovData,
        totalInvoiced,
        isCustomBreakdown,
        invoiceChangesSeperately,
      };

      const hasExistingInvoices =
        sovData.invoiceComponents &&
        sovData.invoiceComponents.length > 0 &&
        sovData.invoiceComponents.some(
          (ic) => ic.qbInvoiceId && ic.status !== "voided",
        );

      if (
        hasExistingInvoices &&
        companyProfile?.plugins?.quickbooks?.connected
      ) {
        const success = await generateQBInvoice(true);
        if (success) {
          await downloadSOVWithInvoices();
        }
      } else {
        await saveSOV(dataToSubmit);
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Submit Error");
      setAlertMessage(`Failed to submit: ${err.message}`);
      setAlertOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvoiceComponentPaymentUpdate = (componentId, field, value) => {
    setSovData((prev) => ({
      ...prev,
      invoiceComponents: prev.invoiceComponents.map((ic) => {
        if (ic._id !== componentId) return ic;

        // When status changes to "paid", auto-fill amountPaid with the full invoice total
        if (field === "status" && value === "paid") {
          const fullAmount = parseFloat(ic.total || ic.amount || 0).toFixed(2);
          return { ...ic, status: "paid", amountPaid: fullAmount };
        }

        // When status changes away from "paid", clear amountPaid back to 0
        // unless they had already entered a partial amount manually
        if (field === "status" && value !== "paid") {
          return { ...ic, status: value, amountPaid: "0.00" };
        }

        // When manually editing amountPaid, also auto-set status based on amount
        if (field === "amountPaid") {
          const paid = parseFloat(value) || 0;
          const total = parseFloat(ic.total || ic.amount || 0);
          const autoStatus =
            paid <= 0
              ? ic.status === "paid"
                ? "sent"
                : ic.status // downgrade from paid if cleared
              : paid >= total
                ? "paid"
                : ic.status; // partial — leave status alone, let user decide
          return { ...ic, amountPaid: value, status: autoStatus };
        }

        return { ...ic, [field]: value };
      }),
    }));
  };
  const saveInvoiceComponentPayment = async (componentId) => {
    try {
      const token = localStorage.getItem("token");
      const component = sovData.invoiceComponents.find(
        (ic) => ic._id === componentId,
      );
      if (!component) return;

      const response = await axios.put(
        `${BASE_URL}/api/sovs2/${sovData._id}/invoice-component/${componentId}/payment`,
        {
          amountPaid: component.amountPaid,
          status: component.status,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Sync with server response
      setSovData((prev) => ({
        ...prev,
        invoiceComponents: response.data.sov.invoiceComponents,
      }));

      // Also update the sov in the sovs list so the summary table reflects it
      setSovs((prev) =>
        prev.map((s) =>
          s._id === sovData._id
            ? { ...s, invoiceComponents: response.data.sov.invoiceComponents }
            : s,
        ),
      );

      setAlertType("success");
      setAlertTitle("Payment Updated");
      setAlertMessage("Invoice payment updated successfully.");
      setAlertOpen(true);
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Payment Update Error");
      setAlertMessage(
        `Failed to update payment: ${err.response?.data?.message || err.message}`,
      );
      setAlertOpen(true);
    }
  };

  const handleCreateSOV = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token");

      setEditingSovId(null);

      const projectResponse = await axios.get(
        `${BASE_URL}/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProject = projectResponse.data;
      setProject(updatedProject);

      let nextDrawNumber = 1;
      try {
        const drawNumberResponse = await axios.get(
          `${BASE_URL}/api/sovs2/project/${project._id}/next-draw-number`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextDrawNumber = drawNumberResponse.data.nextDrawNumber || 1;
      } catch (err) {
        console.error("Failed to fetch next draw number:", err);
        nextDrawNumber = sovs.length + 1;
      }

      let freshApprovedCOs = [];

      if (updatedProject.version === 2) {
        const coResponse = await axios.get(
          `${BASE_URL}/api/changeOrders2/project/${updatedProject._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        freshApprovedCOs = coResponse.data.changeOrders
          .filter(
            (co) =>
              co.summary?.billable === true &&
              (co.approvalStatus === "Approved" ||
                co.approvalStatus === "Confirmed"),
          )
          .map((co) => ({
            ...co,
            createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
          }));

        const pricingSectionMap = {};
        coResponse.data.changeOrders.forEach((co) => {
          if (co.pricingSections && co.pricingSections.length > 0) {
            pricingSectionMap[co.changeOrderNumber] = co.pricingSections;
          }
        });
        setChangeOrderPricingSections(pricingSectionMap);
      } else {
        freshApprovedCOs = (updatedProject.changeOrders || []).filter(
          (co) =>
            co.summary?.billable === true &&
            (co.approvalStatus === "Approved" ||
              co.approvalStatus === "Confirmed"),
        );
      }

      setApprovedChangeOrders(freshApprovedCOs);

      // Sort SOVs by drawNumber ascending to find the most recent one reliably
      const sortedSovs = [...sovs].sort((a, b) => {
        const drawDiff = (a.drawNumber || 0) - (b.drawNumber || 0);
        if (drawDiff !== 0) return drawDiff;
        return (a.createdAt || "").localeCompare(b.createdAt || "");
      });

      const latestSov =
        sortedSovs.length > 0 ? sortedSovs[sortedSovs.length - 1] : null;

      // Split latest SOV items into original vs CO
      const latestOriginalItems = latestSov
        ? latestSov.items.filter((i) => !i.changeOrderNumber)
        : [];
      const latestCoItems = latestSov
        ? latestSov.items.filter((i) => i.changeOrderNumber)
        : [];

      let useCustomBreakdown = false;
      let breakdownForNew = [{ description: "", amount: "0.00", area: "" }];

      if (latestSov) {
        useCustomBreakdown =
          latestOriginalItems.length > 1 || !!latestSov.isCustomBreakdown;
        if (useCustomBreakdown) {
          breakdownForNew = latestOriginalItems.map((i) => ({
            description: i.description,
            amount: i.scheduledValue,
            area: i.area || "",
          }));
        }
      }

      const useInvoiceChangesSeperately =
        latestSov?.invoiceChangesSeperately || false;

      setIsCustomBreakdown(useCustomBreakdown);
      setCustomBreakdownItems(breakdownForNew);
      setInvoiceChangesSeperately(useInvoiceChangesSeperately);
      setHoldbackInvoiced(false);

      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxCodeId ||
          quickBooksTaxRates.find((tr) =>
            tr.taxRates.some((rate) => rate.rate === 0),
          )?.id ||
          quickBooksTaxRates[0]?.id ||
          ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxRate || "0"
        : parseFloat(taxRateData.taxRate) || 0;

      // ═══════════════════════════════════════════════════════════
      // ORIGINAL CONTRACT ITEMS (raw — previous values computed later)
      // ═══════════════════════════════════════════════════════════
      let rawOriginalItems = [];

      if (useCustomBreakdown && breakdownForNew.length > 0) {
        rawOriginalItems = breakdownForNew.map((item, index) => {
          const description = item.description || `Breakdown Item ${index + 1}`;
          const area = item.area || "";
          const scheduledValue = parseFloat(item.amount) || 0;

          return {
            itemNumber: index + 1,
            description,
            area,
            taxCodeId: defaultTaxCode,
            taxRate: defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: scheduledValue.toFixed(2),
            qbItem: getDefaultQbItemId(),
            changeOrderNumber: null,
          };
        });
      } else {
        const scheduledValue =
          parseFloat(updatedProject.originalContractValue) || 0;

        rawOriginalItems = [
          {
            itemNumber: 1,
            description: "Original Contract Value",
            area: "",
            taxCodeId: defaultTaxCode,
            taxRate: defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: scheduledValue.toFixed(2),
            qbItem: getDefaultQbItemId(),
            changeOrderNumber: null,
          },
        ];
      }

      // ═══════════════════════════════════════════════════════════
      // CHANGE ORDER ITEMS — carry forward from latest SOV
      // ═══════════════════════════════════════════════════════════
      const existingCoMap = new Map();
      latestCoItems.forEach((item) => {
        existingCoMap.set(String(item.changeOrderNumber), item);
      });

      const rawExistingCoItems = Array.from(existingCoMap.values()).map(
        (item) => {
          const scheduledValue = parseFloat(item.scheduledValue) || 0;

          return {
            itemNumber: 0,
            description: item.description,
            area: "",
            changeOrderNumber: String(item.changeOrderNumber),
            changeOrderId: item.changeOrderId,
            referenceNumber: item.referenceNumber || null,
            taxCodeId: item.taxCodeId || defaultTaxCode,
            taxRate: item.taxRate || defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: scheduledValue.toFixed(2),
            qbItem: getDefaultQbItemId(),
          };
        },
      );

      // Brand new COs not seen in any previous SOV
      const rawNewCoItems = freshApprovedCOs
        .filter((co) => !existingCoMap.has(String(co.changeOrderNumber)))
        .map((co) => {
          const scheduledValue =
            parseFloat(co.totalCustomerPrice || co.totalPrice) || 0;
          return {
            itemNumber: 0,
            description: co.title,
            area: "",
            changeOrderNumber: String(co.changeOrderNumber),
            changeOrderId: co._id,
            referenceNumber: co.referenceNumber || null,
            taxCodeId: defaultTaxCode,
            taxRate: defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: "0.00",
            workCompletedThisPeriod: "0.00",
            totalCompletedToDate: "0.00",
            percentage: "0.00",
            balanceToFinish: scheduledValue.toFixed(2),
            qbItem: getDefaultQbItemId(),
          };
        });

      // Combine and renumber
      const rawItems = renumberItems([
        ...rawOriginalItems,
        ...rawExistingCoItems,
        ...rawNewCoItems,
      ]);

      // ═══════════════════════════════════════════════════════════
      // COMPUTE PREVIOUS VALUES using positional matching
      // against the most recent prior SOV
      // ═══════════════════════════════════════════════════════════
      const finalItems = computePreviousValues(
        rawItems,
        sortedSovs,
        null, // no current SOV ID since this is a new one
        nextDrawNumber,
      );

      const poNumber = latestSov?.poNumber || "";

      setSovData((prev) => ({
        ...prev,
        billTo: updatedProject.accountId?.companyName || "",
        projectName: updatedProject.projectName || "",
        job: getAddressString(updatedProject.address),
        accountName: updatedProject.accountId?.companyName || "",
        accountAddress: getAddressString(
          updatedProject.accountId?.address || {},
        ),
        sendToEmail: updatedProject.billingContact?.email
          ? [updatedProject.billingContact.email]
          : [],
        withHoldback: latestSov?.withHoldback ?? false,
        specialInstructions: latestSov?.specialInstructions || "",
        items: finalItems,
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        status: "new",
        sentAt: null,
        invoiceDate: "",
        dueDate: "",
        totalInvoiced: "0.00",
        invoiceChangesSeperately: useInvoiceChangesSeperately,
        invoiceComponents: [],
        holdbackInvoiced: false,
        holdbackInvoiceAmount: "0.00",
        poNumber,
        drawNumber: nextDrawNumber,
        workCompletedFrom: "",
        workCompletedTo: "",
      }));

      setIsBreakdownExpanded(false);
      setShowCreateForm(true);
    } catch (err) {
      console.error("Create SOV failed:", err);
      setAlertType("error");
      setAlertTitle("Create SOV Error");
      setAlertMessage(`Failed to create SOV: ${err.message}`);
      setAlertOpen(true);
    }
  };

  const handleInvoiceHoldbackSOV = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token");

      // Fetch fresh project data (same as handleCreateSOV)
      const projectResponse = await axios.get(
        `${BASE_URL}/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProject = projectResponse.data;
      setProject(updatedProject);

      // Get next draw number (same as create)
      let nextDrawNumber = 1;
      try {
        const drawRes = await axios.get(
          `${BASE_URL}/api/sovs2/project/${project._id}/next-draw-number`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        nextDrawNumber = drawRes.data.nextDrawNumber || 1;
      } catch (err) {
        nextDrawNumber = sovs.length + 1;
      }

      const outstanding = calculateOutstandingHoldback();
      if (outstanding <= 0) return;

      // Default values for the holdback release item
      const holdbackItem = {
        itemNumber: 1,
        description: "Release of Retained Holdback",
        area: "",
        changeOrderNumber: null,
        taxCodeId: companyProfile?.defaultTax?.taxCodeId || "NON",
        taxRate: companyProfile?.defaultTax?.taxRate?.toString() || "0",
        scheduledValue: outstanding.toFixed(2),
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod: outstanding.toFixed(2),
        totalCompletedToDate: outstanding.toFixed(2),
        percentage: "100.00",
        balanceToFinish: "0.00",
        qbItem:
          companyProfile?.defaultTax?.defaultItemId || getDefaultQbItemId(),
      };

      // Minimal base SOV data (will be filled in form)
      setSovData({
        ...sovData,
        projectName: project.projectName || "",
        job: getAddressString(project.address),
        accountName: project.accountId?.companyName || "",
        accountAddress: getAddressString(project.accountId?.address || {}),
        sendToEmail: project.billingContact?.email
          ? [project.billingContact.email]
          : [],
        withHoldback: false, // No holdback on holdback release
        poNumber: sovs[0]?.poNumber || "", // carry from first/original
        drawNumber: nextDrawNumber,
        items: [holdbackItem],
        holdbackInvoiced: true,
        holdbackInvoiceAmount: outstanding.toFixed(2),
        status: "new",
        generated: false,
        invoiceDate: "",
        dueDate: "",
        invoiceComponents: [],
      });

      setEditingSovId(null);
      setShowCreateForm(true);

      setAlertType("info");
      setAlertTitle("Holdback Release Ready");
      setAlertMessage(
        `Added holdback release item of ${formatDollarValue(outstanding)}. Review and generate the invoice.`,
      );
      setAlertOpen(true);
    } catch (err) {
      console.error("Holdback SOV error:", err);
      setAlertType("error");
      setAlertMessage(
        "Failed to prepare holdback invoice: " +
          (err.response?.data?.message || err.message),
      );
      setAlertOpen(true);
    }
  };

  const saveSOV = async (dataToSubmit) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setAlertType("error");
      setAlertTitle("Authentication Error");
      setAlertMessage("Please log in to submit SOV.");
      setAlertOpen(true);
      return;
    }

    try {
      const payload = {
        billTo: dataToSubmit.billTo,
        invoiceDate: dataToSubmit.invoiceDate,
        dueDate: dataToSubmit.dueDate,
        job: dataToSubmit.job,
        items: dataToSubmit.items ?? [], // null → [], undefined → []
        withHoldback: !!dataToSubmit.withHoldback,
        status: dataToSubmit.status || "new",
        totalInvoiced: dataToSubmit.totalInvoiced || "0.00",
        isCustomBreakdown: !!dataToSubmit.isCustomBreakdown,
        sendToEmail: Array.isArray(dataToSubmit.sendToEmail)
          ? dataToSubmit.sendToEmail.join(",")
          : dataToSubmit.sendToEmail || "",
        specialInstructions: dataToSubmit.specialInstructions || "",
        invoiceChangesSeperately: !!dataToSubmit.invoiceChangesSeperately,
        holdbackInvoiced: !!dataToSubmit.holdbackInvoiced,
        holdbackInvoiceAmount: dataToSubmit.holdbackInvoiceAmount || "0.00",
        poNumber: dataToSubmit.poNumber || "",
        drawNumber: Number(dataToSubmit.drawNumber) || 1,
        workCompletedFrom: dataToSubmit.workCompletedFrom || "",
        workCompletedTo: dataToSubmit.workCompletedTo || "",
        // add qbInvoiceNumber, qbInvoiceId, etc. only if you want to allow changing them from frontend
      };
      const method = editingSovId ? "PUT" : "POST";
      const url = editingSovId
        ? `${BASE_URL}/api/sovs2/project/${project._id}/${editingSovId}`
        : `${BASE_URL}/api/sovs2/project/${project._id}`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload), // ← send everything
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();
      setSovs((prev) =>
        editingSovId
          ? prev.map((sov) => (sov._id === data._id ? data : sov))
          : [...prev, data],
      );
      setSovData(data);
      setEditingSovId(data._id);
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Save SOV Error");
      setAlertMessage(`Failed to save SOV: ${err.message}`);
      setAlertOpen(true);
    }
  };

  const calculateTotalInvoiced = () => {
    const thisPeriodSubtotal = sovData.items.reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    );
    const hst = sovData.items.reduce((sum, item) => {
      const taxRate = companyProfile?.plugins?.quickbooks?.connected
        ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
            ?.rate || 0
        : parseFloat(item.taxRate) || 0;
      return (
        sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
      );
    }, 0);
    const subTotalWithTax = thisPeriodSubtotal + hst;

    const totalHoldback = sovData.withHoldback ? subTotalWithTax * 0.1 : 0;
    return (subTotalWithTax - totalHoldback).toFixed(2);
  };

  // Helper: Calculate total holdback DEDUCTED (retained) from all previous progress invoices
  const calculateTotalHoldbackDeducted = () => {
    let total = 0;

    sovs.forEach((sov) => {
      // Skip the current SOV being edited/viewed
      if (sov._id === sovData._id) return;

      sov.invoiceComponents?.forEach((comp) => {
        // Only count holdback from progress invoices (exclude release invoices)
        if (comp.component !== "holdback" && comp.holdback) {
          total += parseFloat(comp.holdback) || 0;
        }
      });
    });

    return total;
  };

  // Helper: Calculate total holdback RELEASED (already invoiced/released)
  // Helper: Calculate total holdback RELEASED (already invoiced/released)
  const calculateTotalHoldbackReleased = () => {
    let total = 0;

    sovs.forEach((sov) => {
      // Check invoiceComponents for holdback releases that have been generated to QuickBooks
      sov.invoiceComponents?.forEach((comp) => {
        if (comp.component === "holdback") {
          // Use amount or total — whichever is present
          total += parseFloat(comp.amount || comp.total || "0");
        }
      });

      // Also check for SOVs marked as holdbackInvoiced that may not have invoiceComponents yet
      // (i.e., holdback SOV created but not yet generated to QuickBooks)
      if (
        sov.holdbackInvoiced &&
        parseFloat(sov.holdbackInvoiceAmount || "0") > 0
      ) {
        // Check if this holdback is already counted in invoiceComponents
        const hasHoldbackComponent = sov.invoiceComponents?.some(
          (comp) => comp.component === "holdback",
        );
        if (!hasHoldbackComponent) {
          total += parseFloat(sov.holdbackInvoiceAmount || "0");
        }
      }
    });

    return total;
  };

  // Main function: Net outstanding holdback that can still be invoiced
  const calculateOutstandingHoldback = () => {
    // Only relevant if holdback is enabled on the project

    const deducted = calculateTotalHoldbackDeducted();
    const released = calculateTotalHoldbackReleased();
    const outstanding = Math.max(0, deducted - released);

    return outstanding;
  };

  const availableItems = [
    {
      description: isCustomBreakdown
        ? "Custom Breakdown"
        : "Original Contract Value",
      totalPrice: project.originalContractValue,
    },
    ...approvedChangeOrders.map((co) => ({
      description: `${co.changeOrderNumber} - ${co.title}`,
      totalPrice: co.totalCustomerPrice || co.totalPrice,
    })),
  ];

  const formatDollarValue = (num) =>
    Number.isFinite(parseFloat(num))
      ? `$${parseFloat(num).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "$0.00";

  if (isLoading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  const thisPeriodSubtotal = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    )
    .toFixed(2);
  const totalCompletedToDate = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.totalCompletedToDate) || 0),
      0,
    )
    .toFixed(2);

  const hst = sovData.items
    .reduce((sum, item) => {
      const taxRate = companyProfile?.plugins?.quickbooks?.connected
        ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
            ?.rate || 0
        : parseFloat(item.taxRate) || 0;
      return (
        sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
      );
    }, 0)
    .toFixed(2);
  const subTotalWithTax = (
    parseFloat(thisPeriodSubtotal) + parseFloat(hst)
  ).toFixed(2);
  const currentHoldback = sovData.withHoldback
    ? (parseFloat(subTotalWithTax) * 0.1).toFixed(2)
    : "0.00";
  const totalProjectHoldback = sovData.withHoldback
    ? (
        parseFloat(totalCompletedToDate) *
        (1 +
          (companyProfile?.plugins?.quickbooks?.connected
            ? quickBooksTaxRates.find(
                (tr) => tr.id === companyProfile.defaultTaxRate,
              )?.taxRates[0]?.rate || 0
            : parseFloat(companyProfile?.defaultTaxRate) || 0) /
            100) *
        0.1
      ).toFixed(2)
    : "0.00";
  const remainingBalance = calculateTotalInvoiced();

  const totalScheduledValue = sovData.items
    .reduce((sum, item) => sum + (parseFloat(item.scheduledValue) || 0), 0)
    .toFixed(2);
  const totalWorkCompletedPreviously = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedPreviously) || 0),
      0,
    )
    .toFixed(2);
  const totalWorkCompletedThisPeriod = thisPeriodSubtotal;
  const totalBalanceToFinish = sovData.items
    .reduce((sum, item) => sum + (parseFloat(item.balanceToFinish) || 0), 0)
    .toFixed(2);
  const totalPercentage = totalScheduledValue
    ? (
        (parseFloat(totalCompletedToDate) / parseFloat(totalScheduledValue)) *
        100
      ).toFixed(2)
    : "0.00";

  // Calculate separate subtotals for display
  const baseContractItems = sovData.items.filter(
    (item) => !item.changeOrderNumber,
  );
  const changeOrderItems = sovData.items.filter(
    (item) => item.changeOrderNumber,
  );

  const baseContractDrawSubtotal = baseContractItems
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    )
    .toFixed(2);
  const changeOrderDrawSubtotal = changeOrderItems
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    )
    .toFixed(2);

  let taxRate = 0;

  if (sovData.items.length > 0) {
    if (companyProfile?.plugins?.quickbooks?.connected) {
      // QuickBooks mode: use the tax rate from the FIRST item's tax code
      const firstItemTaxCodeId = sovData.items[0].taxCodeId;
      const matchingTax = quickBooksTaxRates.find(
        (tr) => tr.id === firstItemTaxCodeId,
      );
      taxRate = matchingTax?.taxRates?.[0]?.rate || 0;
    } else {
      // Manual mode: use whatever is stored on the first item
      taxRate = parseFloat(sovData.items[0].taxRate) || 0;
    }
  } else {
    // No items → fall back to company default (safer than 0)
    taxRate = parseFloat(
      companyProfile?.defaultTax?.taxRate || taxRateData.taxRate || 0,
    );
  }

  taxRate = parseFloat(taxRate); // make sure it's a number

  const totalDrawAmount = baseContractDrawSubtotal + changeOrderDrawSubtotal;
  const holdbackAmount = sovData.withHoldback ? totalDrawAmount * 0.1 : 0;
  const netAmount = totalDrawAmount - holdbackAmount;
  const totalTax = netAmount * (taxRate / 100);
  const amountPayable = netAmount + totalTax;

  const isBusy =
    isSubmitting || isGeneratingInvoice || isDownloading || isSendingInvoice;

  return (
    <div className="space-y-6">
      {!showCreateForm && (
        <>
          <div className="flex space-x-4 mb-4"></div>
          <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
            <div className="flex justify-between mb-4 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-200">
                SOVs - {companyProfile?.companyName || "Company"}
              </h3>
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={handleCreateSOV}
                  className="bg-orange-400 text-white px-6 py-3 rounded-lg hover:bg-orange-500 transition font-medium shadow"
                >
                  Create Invoice
                </button>

                {calculateOutstandingHoldback() > 0 && (
                  <button
                    onClick={handleInvoiceHoldbackSOV}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-500 transition font-medium shadow flex items-center gap-3"
                  >
                    <span>Invoice Holdback</span>
                    <span className="text-sm bg-purple-800 px-3 py-1.5 rounded-full">
                      {formatDollarValue(calculateOutstandingHoldback())}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {sovs.length === 0 ? (
              <p className="text-gray-400 px-6 py-4">No SOVs found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                        Draw #
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Job
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Invoice Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {sovs.map((sov) => (
                      <tr
                        key={sov._id}
                        onClick={(e) => {
                          if (e.target.closest(".actions-column")) return;
                          handleViewSOV(sov);
                        }}
                        className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                      >
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          #{sov.drawNumber || "—"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.job}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.invoiceDate}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.dueDate}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.billTo}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 px-6 py-4">
              Available Contract Items
            </h3>
            <p className="text-sm text-gray-400 mb-4 px-6">
              All items are automatically included in new SOVs.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tl-lg">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tr-lg">
                      Total Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {availableItems.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-600 transition-colors duration-200"
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        {item.description}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        {formatDollarValue(item.totalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add this section after the "Available Contract Items" table, before the closing </> of the !showCreateForm block */}

          {/* Invoice Summary Section */}
          <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 px-6 py-4">
              Invoice Summary
            </h3>
            {sovs.flatMap((sov) => sov.invoiceComponents || []).length === 0 ? (
              <p className="text-gray-400 px-6 py-4">
                No invoices generated yet.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tl-lg">
                          Draw #
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                          Component
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                          QB Invoice #
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                          Invoice Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                          Due Date
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Holdback
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Tax
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Total
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                          Paid
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tr-lg">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {sovs
                        .flatMap((sov) =>
                          (sov.invoiceComponents || []).map((comp) => ({
                            ...comp,
                            drawNumber: sov.drawNumber,
                            sovId: sov._id,
                          })),
                        )
                        .sort((a, b) => {
                          // Sort by draw number, then by component type
                          if (a.drawNumber !== b.drawNumber) {
                            return (a.drawNumber || 0) - (b.drawNumber || 0);
                          }
                          // Original contract first, then change orders, then holdback
                          const order = {
                            original_contract: 0,
                            change_order: 1,
                            holdback: 2,
                          };
                          return (
                            (order[a.component] || 0) -
                            (order[b.component] || 0)
                          );
                        })
                        .map((invoice, idx) => (
                          <tr
                            key={`${invoice.sovId}-${idx}`}
                            className="hover:bg-gray-600 transition-colors duration-200"
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              #{invoice.drawNumber || "—"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {invoice.component === "original_contract" &&
                                "Original Contract"}
                              {invoice.component === "change_order" &&
                                (() => {
                                  const matchingCoItem = sovs
                                    .flatMap((s) => s.items || [])
                                    .find(
                                      (item) =>
                                        String(item.changeOrderNumber) ===
                                        String(invoice.changeOrderNumber),
                                    );
                                  const ref =
                                    matchingCoItem?.referenceNumber ||
                                    invoice.referenceNumber;
                                  return ref
                                    ? `CO #${invoice.changeOrderNumber} – ${ref}`
                                    : `CO #${invoice.changeOrderNumber}`;
                                })()}
                              {invoice.component === "holdback" &&
                                "Holdback Release"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-orange-400">
                              {invoice.qbInvoiceNumber || "—"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {invoice.invoiceDate
                                ? new Date(
                                    invoice.invoiceDate,
                                  ).toLocaleDateString()
                                : "—"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                              {invoice.dueDate
                                ? new Date(invoice.dueDate).toLocaleDateString()
                                : "—"}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.amount || 0)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.holdback || 0)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.tax || 0)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 text-right font-medium">
                              {formatDollarValue(invoice.total || 0)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.amountPaid || 0)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  invoice.status === "paid"
                                    ? "bg-green-600 text-white"
                                    : invoice.status === "sent"
                                      ? "bg-blue-600 text-white"
                                      : invoice.status === "voided"
                                        ? "bg-red-600 text-white"
                                        : invoice.status === "generated"
                                          ? "bg-yellow-600 text-white"
                                          : "bg-gray-600 text-white"
                                }`}
                              >
                                {invoice.status || "—"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      {/* Totals Row */}
                      <tr className="bg-gray-800 font-semibold">
                        <td
                          className="px-4 py-3 text-sm text-gray-200"
                          colSpan="5"
                        >
                          Totals
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatDollarValue(
                            sovs
                              .flatMap((sov) => sov.invoiceComponents || [])
                              .reduce(
                                (sum, inv) =>
                                  sum + (parseFloat(inv.amount) || 0),
                                0,
                              ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatDollarValue(
                            sovs
                              .flatMap((sov) => sov.invoiceComponents || [])
                              .reduce(
                                (sum, inv) =>
                                  sum + (parseFloat(inv.holdback) || 0),
                                0,
                              ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatDollarValue(
                            sovs
                              .flatMap((sov) => sov.invoiceComponents || [])
                              .reduce(
                                (sum, inv) => sum + (parseFloat(inv.tax) || 0),
                                0,
                              ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right font-bold text-orange-400">
                          {formatDollarValue(
                            sovs
                              .flatMap((sov) => sov.invoiceComponents || [])
                              .reduce(
                                (sum, inv) =>
                                  sum + (parseFloat(inv.total) || 0),
                                0,
                              ),
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-200 text-right">
                          {formatDollarValue(
                            sovs
                              .flatMap((sov) => sov.invoiceComponents || [])
                              .reduce(
                                (sum, inv) =>
                                  sum + (parseFloat(inv.amountPaid) || 0),
                                0,
                              ),
                          )}
                        </td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Summary Cards */}
                <div className="px-6 py-4 bg-gray-800 border-t border-gray-600">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-xs text-gray-400 uppercase">
                        Total Billed
                      </p>
                      <p className="text-xl font-bold text-gray-200">
                        {formatDollarValue(
                          sovs
                            .flatMap((sov) => sov.invoiceComponents || [])
                            .reduce(
                              (sum, inv) => sum + (parseFloat(inv.total) || 0),
                              0,
                            ),
                        )}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-xs text-gray-400 uppercase">
                        Total Paid
                      </p>
                      <p className="text-xl font-bold text-green-400">
                        {formatDollarValue(
                          sovs
                            .flatMap((sov) => sov.invoiceComponents || [])
                            .reduce(
                              (sum, inv) =>
                                sum + (parseFloat(inv.amountPaid) || 0),
                              0,
                            ),
                        )}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-xs text-gray-400 uppercase">
                        Outstanding Balance
                      </p>
                      <p className="text-xl font-bold text-orange-400">
                        {formatDollarValue(
                          sovs
                            .flatMap((sov) => sov.invoiceComponents || [])
                            .reduce(
                              (sum, inv) =>
                                sum +
                                ((parseFloat(inv.total) || 0) -
                                  (parseFloat(inv.amountPaid) || 0)),
                              0,
                            ),
                        )}
                      </p>
                    </div>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <p className="text-xs text-gray-400 uppercase">
                        Holdback Retained
                      </p>
                      <p className="text-xl font-bold text-purple-400">
                        {formatDollarValue(calculateOutstandingHoldback())}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {showEmailForm && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-600">
            <h2 className="text-xl font-semibold text-gray-200 mb-4">
              Send Email
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  From
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  To
                </label>

                <input
                  type="text"
                  value={
                    Array.isArray(emailForm.to)
                      ? emailForm.to.join(", ")
                      : emailForm.to
                  }
                  onChange={(e) =>
                    setEmailForm({
                      ...emailForm,
                      to: e.target.value
                        .split(",")
                        .map((em) => em.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="recipient@example.com, another@example.com"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Body
                </label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, body: e.target.value })
                  }
                  rows="5"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Signature
                </label>
                <textarea
                  value={emailForm.signature}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, signature: e.target.value })
                  }
                  rows="3"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailForm(false);
                    setPdfBuffer(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEmailSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  disabled={!pdfBuffer}
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        type={alertType}
        title={alertTitle}
        message={alertMessage}
        confirmText="OK"
        cancelText="Close"
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => setAlertOpen(false)}
      />

      {showCreateForm && (
        <div className="bg-gray-700 p-6 rounded-lg shadow-xl border border-gray-600">
          {/* Inside the {showCreateForm && ( ... )} block, right after the <h3> */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-200 mb-0">
              {editingSovId ? "Edit SOV" : "Create Schedule of Values"}
            </h3>

            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-5 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-600 hover:bg-gray-500 rounded-md transition-colors"
            >
              ← Back to List
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* NEW HEADER FIELDS SECTION */}
            <div className="bg-gray-800 p-4 rounded-lg mb-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">
                Progress Draw Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Contract PO#
                  </label>
                  <input
                    type="text"
                    name="poNumber"
                    value={sovData.poNumber}
                    onChange={handleSOVChange}
                    disabled={sovData.status === "voided"}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                    placeholder="Enter PO Number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Progress Draw #
                  </label>
                  <input
                    type="number"
                    name="drawNumber"
                    value={sovData.drawNumber}
                    onChange={(e) =>
                      setSovData((prev) => ({
                        ...prev,
                        drawNumber: parseInt(e.target.value) || 1,
                      }))
                    }
                    disabled={sovData.status === "voided"}
                    min="1"
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Work Completed From
                  </label>
                  <input
                    type="date"
                    required
                    name="workCompletedFrom"
                    value={sovData.workCompletedFrom}
                    onChange={handleSOVChange}
                    disabled={sovData.status === "voided"}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Work Completed To
                  </label>
                  <input
                    required
                    type="date"
                    name="workCompletedTo"
                    value={sovData.workCompletedTo}
                    onChange={handleSOVChange}
                    disabled={sovData.status === "voided"}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Bill To
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  name="billTo"
                  value={sovData.billTo}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Special Instructions
                </label>
                <textarea
                  name="specialInstructions"
                  value={sovData.specialInstructions}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  rows="4"
                  placeholder="Enter any special instructions for the invoice"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Invoice Date
                </label>
                <input
                  autoComplete="off"
                  type="date"
                  name="invoiceDate"
                  value={sovData.invoiceDate}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Total Invoiced
                </label>
                <input
                  type="text"
                  value={formatDollarValue(calculateTotalInvoiced())}
                  readOnly
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={sovData.dueDate}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Job
                </label>
                <input
                  type="text"
                  name="job"
                  value={sovData.job}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Status
                </label>
                <select
                  name="status"
                  value={sovData.status}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                >
                  <option value="new">New</option>
                  <option value="invoiced">Invoiced</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="voided">Voided</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Include Holdback
                </label>
                <input
                  type="checkbox"
                  checked={sovData.withHoldback}
                  onChange={(e) =>
                    setSovData({ ...sovData, withHoldback: e.target.checked })
                  }
                  disabled={sovData.status === "voided"}
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Send To Email
                </label>
                <Select
                  isMulti
                  options={contactOptions}
                  value={contactOptions.filter((o) =>
                    (Array.isArray(sovData.sendToEmail)
                      ? sovData.sendToEmail
                      : [sovData.sendToEmail]
                    ).includes(o.value),
                  )}
                  onChange={(selected) =>
                    setSovData((prev) => ({
                      ...prev,
                      sendToEmail: selected ? selected.map((o) => o.value) : [],
                    }))
                  }
                  placeholder="Select contacts..."
                  styles={customStyles}
                  isDisabled={sovData.status === "voided"}
                  noOptionsMessage={() =>
                    contacts.length === 0
                      ? "Loading contacts..."
                      : "No contacts found"
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Custom Breakdown for Original Contract
                </label>
                <input
                  type="checkbox"
                  checked={isCustomBreakdown}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsCustomBreakdown(checked);

                    if (!checked) {
                      setCustomBreakdownItems([
                        { description: "", amount: "0.00" },
                      ]);

                      const originalContractValue =
                        parseFloat(project.originalContractValue) || 0;
                      const defaultTaxCode = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? taxRateData.taxCodeId ||
                          quickBooksTaxRates.find((tr) =>
                            tr.taxRates.some((rate) => rate.rate === 0),
                          )?.id ||
                          quickBooksTaxRates[0]?.id ||
                          ""
                        : "";
                      const defaultTaxRate = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? taxRateData.taxRate || "0"
                        : parseFloat(taxRateData.taxRate) || 0;
                      const defaultItemId = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? companyProfile.defaultTax?.defaultItemId ||
                          quickBooksItems[0]?.id ||
                          ""
                        : "";

                      const prevInvoiced = calculatePreviousInvoiced(
                        "Original Contract Value",
                        editingSovId,
                      );

                      const workThis = editingSovId
                        ? sovData.items.find(
                            (i) => i.description === "Original Contract Value",
                          )?.workCompletedThisPeriod || "0.00"
                        : "0.00";
                      const totalCompleted = (
                        prevInvoiced + parseFloat(workThis)
                      ).toFixed(2);

                      const originalItem = {
                        itemNumber: 1,
                        description: "Original Contract Value",
                        taxCodeId: defaultTaxCode,
                        taxRate: defaultTaxRate.toString(),
                        scheduledValue: originalContractValue.toFixed(2),
                        workCompletedPreviously: prevInvoiced.toFixed(2),
                        workCompletedThisPeriod: workThis,
                        totalCompletedToDate: totalCompleted,
                        percentage: originalContractValue
                          ? (
                              (parseFloat(totalCompleted) /
                                originalContractValue) *
                              100
                            ).toFixed(2)
                          : "0.00",
                        balanceToFinish: (
                          originalContractValue - parseFloat(totalCompleted)
                        ).toFixed(2),
                        qbItem: getDefaultQbItemId(),
                        changeOrderNumber: null,
                      };

                      const existingCoNumbers = new Set(
                        sovData.items
                          .filter((i) => i.changeOrderNumber)
                          .map((i) => String(i.changeOrderNumber)),
                      );

                      const changeOrderItems = approvedChangeOrders
                        .filter(
                          (co) =>
                            !existingCoNumbers.has(
                              String(co.changeOrderNumber),
                            ),
                        )
                        .map((co, idx) => {
                          const description = `${co.changeOrderNumber} - ${co.title}`;
                          const scheduledValue =
                            parseFloat(
                              co.totalCustomerPrice || co.totalPrice,
                            ) || 0;
                          const prevInvoiced = calculatePreviousInvoiced(
                            description,
                            editingSovId,
                          );

                          const workThis = "0.00";
                          const totalCompleted = (
                            prevInvoiced + parseFloat(workThis)
                          ).toFixed(2);

                          return {
                            itemNumber: 1 + idx + 1,
                            description,
                            changeOrderNumber: String(co.changeOrderNumber),
                            changeOrderId: co._id,
                            referenceNumber: null,
                            taxCodeId: defaultTaxCode,
                            taxRate: defaultTaxRate.toString(),
                            scheduledValue: scheduledValue.toFixed(2),
                            workCompletedPreviously: prevInvoiced.toFixed(2),
                            workCompletedThisPeriod: workThis,
                            totalCompletedToDate: totalCompleted,
                            percentage: scheduledValue
                              ? (
                                  (parseFloat(totalCompleted) /
                                    scheduledValue) *
                                  100
                                ).toFixed(2)
                              : "0.00",
                            balanceToFinish: (
                              scheduledValue - parseFloat(totalCompleted)
                            ).toFixed(2),
                            qbItem: getDefaultQbItemId(),
                          };
                        });

                      setSovData((prev) => ({
                        ...prev,
                        items: [originalItem, ...changeOrderItems],
                      }));

                      setIsBreakdownExpanded(false);
                    }
                  }}
                  disabled={sovData.status === "voided"}
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>

            {isCustomBreakdown && (
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-400">
                    Original Contract Breakdown (Total:{" "}
                    {formatDollarValue(project.originalContractValue)})
                  </h4>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-400">
                      Remaining:{" "}
                      {formatDollarValue(calculateRemainingBreakdown())}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setIsBreakdownExpanded(!isBreakdownExpanded)
                      }
                      disabled={sovData.status === "voided"}
                      className={`text-sm font-medium ${
                        sovData.status === "voided"
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-orange-400 hover:text-orange-300"
                      } transition-colors duration-200`}
                    >
                      {isBreakdownExpanded ? "Collapse ▼" : "Expand ▲"}
                    </button>
                  </div>
                </div>
                {isBreakdownExpanded && (
                  <div className="space-y-2 mt-2">
                    {/* Add header row */}
                    <div className="flex space-x-2 items-center font-semibold text-gray-300 text-sm">
                      <span className="flex-1">Description</span>
                      <span className="w-32">Area</span>
                      <span className="w-32">Amount</span>
                      <span className="w-8"></span>
                    </div>

                    {customBreakdownItems.map((item, index) => {
                      const sovItem = sovData.items.find(
                        (sovItem) =>
                          sovItem.description ===
                          (item.description || `Breakdown Item ${index + 1}`),
                      );
                      const hasProgress =
                        sovItem &&
                        ((parseFloat(sovItem.workCompletedPreviously) || 0) >
                          0 ||
                          (parseFloat(sovItem.workCompletedThisPeriod) || 0) >
                            0);
                      return (
                        <div
                          key={index}
                          className="flex space-x-2 items-center"
                        >
                          <input
                            type="text"
                            placeholder="Description (e.g., First Floor)"
                            value={item.description}
                            onChange={(e) =>
                              handleBreakdownItemChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            disabled={sovData.status === "voided"}
                            className="flex-1 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          />
                          <input
                            type="text"
                            placeholder="Area (e.g., Living Room)"
                            value={item.area || ""}
                            onChange={(e) =>
                              handleBreakdownItemChange(
                                index,
                                "area",
                                e.target.value,
                              )
                            }
                            disabled={sovData.status === "voided"}
                            className="w-32 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          />
                          <NumericFormat
                            value={item.amount}
                            onValueChange={(values) =>
                              handleBreakdownItemChange(
                                index,
                                "amount",
                                values.floatValue || "0.00",
                              )
                            }
                            thousandSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            disabled={sovData.status === "voided"}
                            className="w-32 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                            placeholder="$0.00"
                          />
                          <button
                            type="button"
                            onClick={() => removeBreakdownItem(index)}
                            disabled={
                              sovData.status === "voided" ||
                              customBreakdownItems.length === 1 ||
                              hasProgress
                            }
                            className="text-red-400 hover:text-red-300 disabled:text-gray-400 transition-colors duration-200"
                          >
                            🗑️
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={addBreakdownItem}
                      disabled={sovData.status === "voided"}
                      className={`text-sm font-medium ${
                        sovData.status === "voided"
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-orange-400 hover:text-orange-300"
                      } transition-colors duration-200`}
                    >
                      + Add Breakdown Item
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Invoice Change Orders Separately
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={invoiceChangesSeperately}
                  onChange={(e) => {
                    setInvoiceChangesSeperately(e.target.checked);
                    setSovData((prev) => ({
                      ...prev,
                      invoiceChangesSeperately: e.target.checked,
                    }));
                  }}
                  disabled={sovData.status === "voided"}
                  className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">
                  Create separate QuickBooks invoices for each change order with
                  work completed
                </span>
              </div>
            </div>

            {holdbackInvoiced && sovData.holdbackInvoiceAmount && (
              <div className="mt-4 p-4 border border-green-600 rounded-lg bg-gray-800">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">✓</span>
                  <span className="text-sm text-gray-300">
                    Holdback marked for invoice:{" "}
                    {formatDollarValue(sovData.holdbackInvoiceAmount)}
                  </span>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-4 rounded-tl-lg">
                      Item #
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-48">
                      Description
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Reference
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      %
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-28">
                      Tax Rate
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Scheduled Value
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Work Completed (Prev)
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Work Completed (This)
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Total Completed
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Balance to Finish
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-24 rounded-tr-lg">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {sovData.items.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-600 transition-colors duration-200"
                    >
                      <td className="px-2 py-2 text-sm text-gray-200 w-12">
                        {item.itemNumber}
                      </td>
                      <td className="px-2 py-2 w-48">
                        <textarea
                          value={buildItemDescription(item)}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          disabled={
                            sovData.status === "voided" ||
                            item.changeOrderNumber
                          }
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50 min-h-[4rem] resize-y"
                          rows="3"
                          placeholder="Enter description"
                        />
                      </td>
                      <td className="px-2 py-2 w-32">
                        {item.changeOrderNumber ? (
                          // For change orders: show reference number
                          <input
                            type="text"
                            value={item.referenceNumber || ""}
                            readOnly
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                            placeholder="N/A"
                          />
                        ) : (
                          // For original contract: show area
                          <input
                            type="text"
                            value={item.area || ""}
                            readOnly
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                            placeholder="No area"
                          />
                        )}
                      </td>
                      <td className="px-2 py-2 w-16">
                        <NumericFormat
                          value={item.percentage}
                          onValueChange={(values) =>
                            handleItemChange(
                              index,
                              "percentage",
                              values.floatValue || "0.00",
                            )
                          }
                          decimalScale={2}
                          fixedDecimalScale
                          suffix="%"
                          disabled={sovData.status === "voided"}
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          placeholder="0.00%"
                        />
                      </td>
                      <td className="px-2 py-2 w-20">
                        {companyProfile?.plugins?.quickbooks?.connected ? (
                          <select
                            value={item.taxCodeId}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "taxCodeId",
                                e.target.value,
                              )
                            }
                            disabled={sovData.status === "voided"}
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          >
                            {quickBooksTaxRates.map((tax) => (
                              <option key={tax.id} value={tax.id}>
                                {`${tax.name} (${tax.taxRates[0]?.rate || 0}%)`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <NumericFormat
                            value={item.taxRate}
                            onValueChange={(values) =>
                              handleItemChange(
                                index,
                                "taxRate",
                                values.floatValue || "0.00",
                              )
                            }
                            decimalScale={2}
                            fixedDecimalScale
                            disabled={sovData.status === "voided"}
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                            placeholder="0.00"
                          />
                        )}
                      </td>
                      <td className="px-2 py-2 w-28">
                        <NumericFormat
                          value={item.scheduledValue}
                          onValueChange={(values) =>
                            handleItemChange(
                              index,
                              "scheduledValue",
                              values.floatValue || "0.00",
                            )
                          }
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$"
                          disabled={sovData.status === "voided"}
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          placeholder="$0.00"
                        />
                      </td>
                      <td className="px-2 py-2 w-28">
                        <input
                          type="text"
                          value={formatDollarValue(
                            item.workCompletedPreviously,
                          )}
                          readOnly
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                        />
                      </td>
                      <td className="px-2 py-2 w-28">
                        <input
                          type="text"
                          value={formatDollarValue(
                            item.workCompletedThisPeriod,
                          )}
                          readOnly
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                        />
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-200 w-28">
                        {formatDollarValue(item.totalCompletedToDate)}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-200 w-28">
                        {formatDollarValue(item.balanceToFinish)}
                      </td>
                      <td className="px-2 py-2 flex space-x-2 w-24">
                        <button
                          type="button"
                          onClick={() => moveItemUp(index)}
                          disabled={index === 0 || sovData.status === "voided"}
                          className="text-orange-400 hover:text-orange-300 disabled:text-gray-400 transition-colors duration-200 mt-8"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItemDown(index)}
                          disabled={
                            index === sovData.items.length - 1 ||
                            sovData.status === "voided"
                          }
                          className="text-orange-400 hover:text-orange-300 disabled:text-gray-400 transition-colors duration-200 mt-8"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          disabled={sovData.status === "voided"}
                          className="text-red-400 hover:text-red-300 disabled:text-gray-400 transition-colors duration-200 mt-8"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-2 py-2 text-sm text-gray-200 w-12"></td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-48">
                      Total
                    </td>
                    <td className="px-2 py-2 w-32"></td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-16">
                      {totalPercentage}%
                    </td>
                    <td className="px-2 py-2 w-20"></td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalScheduledValue)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalWorkCompletedPreviously)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalWorkCompletedThisPeriod)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalCompletedToDate)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalBalanceToFinish)}
                    </td>
                    <td className="px-2 py-2 w-24"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {editingSovId &&
              sovData.invoiceComponents &&
              sovData.invoiceComponents.length > 0 && (
                <div className="mt-6 overflow-hidden rounded-lg shadow-xl bg-gray-700">
                  <h4 className="text-md font-semibold text-gray-300 px-4 py-3 bg-gray-800">
                    QuickBooks Invoices Generated
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tl-lg">
                            Component
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                            QB Invoice #
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                            Subtotal
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                            Holdback
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                            Tax
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase text-orange-300">
                            Total (incl. tax)
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                            Amount Paid
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                            Status
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase rounded-tr-lg">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-600">
                        {sovData.invoiceComponents.map((invoice, idx) => (
                          <tr
                            key={idx}
                            className="hover:bg-gray-600 transition-colors duration-200"
                          >
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {invoice.component === "original_contract" &&
                                "Original Contract"}
                              {invoice.component === "change_order" &&
                                `CO #${invoice.changeOrderNumber}`}
                              {invoice.component === "holdback" && "Holdback"}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-orange-400">
                              {invoice.qbInvoiceNumber || "Not Generated"}
                            </td>

                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.amount || 0)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.holdback || 0)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200 text-right">
                              {formatDollarValue(invoice.tax || 0)}
                            </td>
                            <td className="px-4 py-2 text-sm font-semibold text-orange-300 text-right">
                              {formatDollarValue(invoice.total || 0)}
                            </td>
                            <td className="px-4 py-2 w-36">
                              <NumericFormat
                                value={invoice.amountPaid || "0"}
                                onValueChange={(values) =>
                                  handleInvoiceComponentPaymentUpdate(
                                    invoice._id,
                                    "amountPaid",
                                    values.floatValue?.toFixed(2) || "0.00",
                                  )
                                }
                                thousandSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                disabled={invoice.status === "voided"}
                                className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:opacity-50"
                                placeholder="$0.00"
                              />
                            </td>
                            <td className="px-4 py-2 w-48">
                              <select
                                value={invoice.status || "generated"}
                                onChange={(e) =>
                                  handleInvoiceComponentPaymentUpdate(
                                    invoice._id,
                                    "status",
                                    e.target.value,
                                  )
                                }
                                disabled={invoice.status === "voided"}
                                className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:opacity-50"
                              >
                                <option value="generated">Generated</option>
                                <option value="sent">Sent</option>
                                <option value="paid">Paid</option>
                                <option value="voided">Voided</option>
                              </select>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-200">
                              {invoice.invoiceDate
                                ? new Date(
                                    invoice.invoiceDate,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() =>
                                  saveInvoiceComponentPayment(invoice._id)
                                }
                                disabled={invoice.status === "voided"}
                                className={`px-3 py-1 text-xs font-medium text-white rounded-md transition-colors duration-200 ${
                                  invoice.status === "voided"
                                    ? "bg-gray-500 cursor-not-allowed opacity-50"
                                    : "bg-green-600 hover:bg-green-500"
                                }`}
                              >
                                Save
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {/* Legacy Subtotals Section (kept for backward compatibility) */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-400">
                Additional Details
              </h4>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-400">
                    This Period Subtotal:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(thisPeriodSubtotal)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm text-gray-400">
                    Sub-Total with Tax:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(subTotalWithTax)}
                  </span>
                </div>
                {sovData.withHoldback && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400">
                        Current Holdback (10%):
                      </label>
                      <span className="text-gray-200">
                        {formatDollarValue(currentHoldback)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400">
                        Total Project Holdback:
                      </label>
                      <span className="text-gray-200">
                        {formatDollarValue(totalProjectHoldback)}
                      </span>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm text-gray-400">
                    Remaining Balance:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(remainingBalance)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  window.scrollTo(0, 0);
                }}
                disabled={isBusy}
                className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md transition-colors duration-200 ${
                  isBusy
                    ? "bg-gray-600 cursor-not-allowed opacity-50"
                    : "bg-gray-600 hover:bg-gray-500"
                }`}
              >
                Cancel
              </button>
              {editingSovId && (
                <>
                  <button
                    type="button"
                    onClick={downloadSOVWithInvoices}
                    disabled={sovData.status === "voided" || isBusy}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      sovData.status === "voided" || isBusy
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-orange-400 hover:bg-orange-500"
                    } transition-colors duration-200`}
                  >
                    {isDownloading
                      ? "Downloading..."
                      : sovData.invoiceComponents?.some(
                            (ic) => ic.qbInvoiceId && ic.status !== "voided",
                          )
                        ? "Download Invoice(s)"
                        : "Download SOV"}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendEmail}
                    disabled={sovData.status === "voided" || isBusy}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      sovData.status === "voided" || isBusy
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-orange-400 hover:bg-orange-500"
                    } transition-colors duration-200`}
                  >
                    Send Email
                  </button>
                  {companyProfile?.plugins?.quickbooks?.connected && (
                    <>
                      {(!sovData.invoiceComponents ||
                        sovData.invoiceComponents.length === 0 ||
                        sovData.invoiceComponents.every(
                          (ic) => ic.status === "voided",
                        )) && (
                        <button
                          type="button"
                          onClick={() => generateQBInvoice(false)}
                          disabled={sovData.status === "voided" || isBusy}
                          className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                            sovData.status === "voided" || isBusy
                              ? "bg-gray-600 cursor-not-allowed opacity-50"
                              : "bg-orange-400 hover:bg-orange-500"
                          } transition-colors duration-200`}
                        >
                          Generate QB Invoices
                        </button>
                      )}
                      {sovData.invoiceComponents &&
                        sovData.invoiceComponents.length > 0 && (
                          <button
                            type="button"
                            onClick={voidQBInvoice}
                            disabled={sovData.status === "voided" || isBusy}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                              sovData.status === "voided" || isBusy
                                ? "bg-gray-600 cursor-not-allowed opacity-50"
                                : "bg-red-400 hover:bg-red-500"
                            } transition-colors duration-200`}
                          >
                            Void QB Invoice
                          </button>
                        )}
                    </>
                  )}
                </>
              )}
              <button
                type="submit"
                disabled={sovData.status === "voided" || isBusy}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  sovData.status === "voided" || isBusy
                    ? "bg-gray-600 cursor-not-allowed opacity-50"
                    : "bg-orange-400 hover:bg-orange-500"
                } transition-colors duration-200`}
              >
                {isSubmitting
                  ? "Processing..."
                  : isGeneratingInvoice
                    ? "Updating..."
                    : sovData.invoiceComponents?.some(
                          (ic) => ic.status !== "voided",
                        )
                      ? "Update SOV & QB Invoices"
                      : editingSovId
                        ? "Update SOV"
                        : "Save SOV"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
